<template>
  <modal name="modal-admin-order-alarm" class="modal-inner modal-admin-order-alarm" transition="pop-out" :width="modalWidth" :focus-trap="true" :min-height="600" height="auto" :scrollable="true" :resizable="true" :adaptive="true" :reset="true" :clickToClose="false" @before-close="resetForm">
    <LoadingSpinner :isLoading="isLoading" />
    <span class="close-button" @click="hide('modal-admin-order-alarm')"><BaseIcon icon="times-circle" class="text-white"/></span>
    <div class="modal-header">
      {{ $t('admin_order_alarm.modal_title') }} <span v-if="customer">: {{ customer.name }} </span><span v-if="unit">- {{ unit.name }}</span>
    </div>
    <ValidationObserver ref="formAdminOrderAlarm">
      <div class="modal-body">
        <!-- progress checklist + assignee for existing orders -->
        <div class="flex flex-wrap border-b border-secondary-500 mx-2 pb-4 mb-5 mt-2" v-if="order_alarm._id">
          <div class="w-full px-2 pb-2 flex items-center justify-between">
            <span class="text-gray-600 text-md font-bold font-serif">{{ $t('order_log.progress_checklist_title') }}</span>
            <button
              v-if="user.role === 'admin'"
              type="button"
              class="text-sm text-blue-600 underline"
              :disabled="progressSaving"
              :class="{ 'opacity-50 cursor-not-allowed': progressSaving }"
              @click="setAllProgressItems(!allProgressChecked)"
            >
              {{ allProgressChecked ? $t('order_log.uncheck_all') : $t('order_log.check_all') }}
            </button>
          </div>
          <div class="w-full md:w-1/2 px-2 py-1 flex items-center" v-for="key in progressGeneralKeys" :key="`progress-${key}`">
            <input
              type="checkbox"
              :id="`progress-check-${key}`"
              :checked="isProgressItemChecked(key)"
              :disabled="user.role !== 'admin' || progressSaving"
              @change="toggleProgressItem(key, $event.target.checked)"
              class="mr-2"
              :class="{ 'cursor-pointer': user.role === 'admin' }"
            />
            <label :for="`progress-check-${key}`" class="text-sm text-gray-800" :class="{ 'cursor-pointer': user.role === 'admin' }">
              {{ $t(`order_log.progress_checklist_items.${key}`) }}
            </label>
          </div>

          <template v-for="group in progressDeviceGroups">
            <div :key="`progress-group-${group.key}`" class="w-full px-2 pt-3 pb-1 text-sm font-bold text-gray-700">
              {{ $t(`order_log.device_types.${group.key}`) }}
            </div>
            <div class="w-full md:w-1/2 px-2 py-1 flex items-center" v-for="entry in group.entries" :key="`progress-${entry.key}`">
              <input
                type="checkbox"
                :id="`progress-check-${entry.key}`"
                :checked="isProgressItemChecked(entry.key)"
                :disabled="user.role !== 'admin' || progressSaving"
                @change="toggleProgressItem(entry.key, $event.target.checked)"
                class="mr-2"
                :class="{ 'cursor-pointer': user.role === 'admin' }"
              />
              <label :for="`progress-check-${entry.key}`" class="text-sm text-gray-800" :class="{ 'cursor-pointer': user.role === 'admin' }">
                {{ progressItemLabel(entry.item) }}
              </label>
            </div>
          </template>

          <div v-if="unmatchedDevices.length" class="w-full mx-2 mt-3 flex items-start bg-yellow-100 text-yellow-800 text-sm rounded px-3 py-2">
            <BaseIcon icon="exclamation-triangle" class="mr-2 mt-0.5" />
            <span>{{ $t('order_log.device_items_missing') }}: {{ unmatchedDevices.join(', ') }}</span>
          </div>

          <div class="w-full px-2 my-4">
            <div class="border-b border-secondary-400"></div>
          </div>

          <div class="w-full md:w-1/3 px-2" v-if="user.role === 'admin'">
            <label class="block text-gray-600 text-md font-bold pb-2 font-serif">{{ $t('order_log.assignee') }}</label>
            <select
              v-model="selectedAssignee"
              @change="saveAssignee"
              :disabled="supportStaffLoadFailed"
              class="bg-gray-100 rounded w-full text-sm text-gray-900 focus:outline-none border-b-2 border-gray-300 transition duration-500 px-3 py-2"
            >
              <option value="">{{ $t('order_log.no_assignee') }}</option>
              <option v-for="s in supportStaff" :key="s._id" :value="s._id">{{ s.name }}</option>
            </select>
          </div>
          <div class="w-full md:w-1/3 px-2" v-else>
            <label class="block text-gray-600 text-md font-bold pb-2 font-serif">{{ $t('order_log.assignee') }}</label>
            <p class="text-sm text-gray-800">{{ order_alarm.assignee_name || $t('order_log.not_specified') }}</p>
          </div>

          <!-- device detection + one-click creation (SEC-216, admin only) -->
          <template v-if="user.role === 'admin'">
            <div class="w-full px-2 my-4">
              <div class="border-b border-secondary-400"></div>
            </div>
            <AutomaticDeviceCreationPanel :key="order_alarm._id" :orderId="order_alarm._id" :sellers="orderSellers" />
          </template>
        </div>

        <div class="flex flex-wrap border-b border-secondary-500 mx-2 pb-4 mb-5" v-if="!order_alarm._id">
          <div class="w-full px-2">
            <div class="clearfix">
              <div class="float-right">
                <label class="btn-blue cursor-pointer">
                  {{ $t('admin_order_alarm.order_document') }}
                  <BaseIcon icon="file-pdf" class="ml-1" />
                  <input type="file" accept="application/pdf" ref="pdfOrder" @change="fileChangeCreate()" class="hidden" />
                </label>
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-wrap mt-4">
          <div class="w-full text-gray-600 text-md font-bold px-2 pb-3 font-serif">
            {{ $t('admin_order_alarm.orderer_info') }}
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="order_alarm.customer_info.contact_name" type="text" :field_name="$t('admin_order_alarm.name')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="order_alarm.customer_info.contact_email" type="text" :field_name="$t('admin_order_alarm.email')" rules="required|email" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="order_alarm.customer_info.contact_phone" type="text" :field_name="$t('admin_order_alarm.phone')" rules="required" />
          </div>
          <div class="w-full md:w-1/2 px-2 py-2">
            <BaseTextarea rows="3" v-model="order_alarm.customer_info.company_name" :field_name="$t('admin_order_alarm.company_name')" rules="required" />
          </div>
          <div class="w-full md:w-1/2 px-2 py-2">
            <BaseTextarea rows="3" v-model="order_alarm.customer_info.invoice_detail" :field_name="$t('admin_order_alarm.invoice_address')" rules="required" />
          </div>
        </div>

        <div class="w-full mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap mt-4">
          <div class="w-full text-gray-600 text-md font-bold px-2 pb-3 font-serif">
            {{ $t('admin_order_alarm.general_questions') }}
          </div>
          <div class="w-full md:w-1/2 px-2 py-2">
            <ValidationProvider :name="$t('admin_order_alarm.outdoor_work')" rules="required" v-slot="{ classes, errors }">
              <div class="input-validate" :class="classes">
                <label class="text-gray-600 text-xs pb-2 font-serif">{{ $t('admin_order_alarm.outdoor_work') }}<b class="text-red-500 ml-1">*</b></label>
                <input type="text" v-model="order_alarm.customer_info.outdoor_work" class="bg-gray-100 rounded w-full text-sm text-gray-900 focus:outline-none border-b-2 border-gray-300 transition duration-500 px-3 pt-1 pb-1" />
                <span>{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>
          <div class="w-full md:w-1/2 px-2 py-2">
            <ValidationProvider :name="$t('admin_order_alarm.alert_colleagues')" rules="required" v-slot="{ classes, errors }">
              <div class="input-validate" :class="classes">
                <label class="text-gray-600 text-xs pb-2 font-serif">{{ $t('admin_order_alarm.alert_colleagues') }}<b class="text-red-500 ml-1">*</b></label>
                <input type="text" v-model="order_alarm.customer_info.alert_members" class="bg-gray-100 rounded w-full text-sm text-gray-900 focus:outline-none border-b-2 border-gray-300 transition duration-500 px-3 pt-1 pb-1" />
                <span>{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>
          <div class="w-full md:w-1/2 px-2 py-2">
            <ValidationProvider :name="$t('admin_order_alarm.indoor_location')" rules="required" v-slot="{ classes, errors }">
              <div class="input-validate" :class="classes">
                <label class="text-gray-600 text-xs pb-2 font-serif">{{ $t('admin_order_alarm.indoor_location') }}<b class="text-red-500 ml-1">*</b></label>
                <input type="text" v-model="order_alarm.customer_info.indoor_tracking" class="bg-gray-100 rounded w-full text-sm text-gray-900 focus:outline-none border-b-2 border-gray-300 transition duration-500 px-3 pt-1 pb-1" />
                <span>{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>
        </div>

        <!-- demo feedback: notifications on alarm — general SMS/email contacts
             and/or a free-text description of an individual setup. The
             structured contacts (SEC-221 REQ-04) moved in here from the
             general questions above. -->
        <div class="w-full mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap mt-4">
          <div class="w-full text-gray-600 text-md font-bold px-2 pb-3 font-serif">
            {{ $t('admin_order_alarm.notifications_section') }}
          </div>
          <div class="w-full px-2 pb-1">
            <label class="inline-flex items-center text-sm text-gray-800 cursor-pointer">
              <input type="checkbox" class="form-checkbox h-4 w-4" v-model="order_alarm.notify_general" />
              <span class="ml-2 font-semibold">{{ $t('admin_order_alarm.notifications_general') }}</span>
            </label>
            <p class="text-xs italic text-gray-500 ml-6">{{ $t('admin_order_alarm.notifications_general_desc') }}</p>
          </div>
          <div class="w-full px-2 py-2" v-if="order_alarm.notify_general">
            <div class="w-full md:w-2/3 flex flex-wrap mt-1" v-for="(contact, i) in notificationContacts" :key="`notification-${i}`">
              <div class="w-5/12 pr-1">
                <ValidationProvider :name="`${$t('admin_order_alarm.email')} ${i + 1}`" rules="email" v-slot="{ classes, errors }">
                  <div class="input-validate" :class="classes">
                    <input v-model.trim="contact.email" type="text" :placeholder="$t('admin_order_alarm.email')" class="bg-gray-100 rounded w-full text-sm text-gray-900 focus:outline-none border-b-2 border-gray-300 transition duration-500 px-3 py-2" />
                    <span>{{ errors[0] }}</span>
                  </div>
                </ValidationProvider>
              </div>
              <div class="w-5/12 px-1">
                <ValidationProvider :name="`${$t('admin_order_alarm.phone')} ${i + 1}`" rules="sms_msisdn" :customMessages="{ sms_msisdn: $t('admin_order_alarm.sms_number_format') }" v-slot="{ classes, errors }">
                  <div class="input-validate" :class="classes">
                    <input v-model.trim="contact.msisdn" type="text" @keypress="isNumber($event)" maxlength="15" :placeholder="$t('admin_order_alarm.phone')" class="bg-gray-100 rounded w-full text-sm text-gray-900 focus:outline-none border-b-2 border-gray-300 transition duration-500 px-3 py-2" />
                    <span>{{ errors[0] }}</span>
                  </div>
                </ValidationProvider>
              </div>
              <button class="w-1/12 text-red-500 pl-1" v-if="notificationContacts.length > 1" @click.prevent="removeNotificationContact(i)"><BaseIcon icon="minus-circle" /></button>
            </div>
            <button class="btn-blue-outline mt-2" @click.prevent="addNotificationContact()">{{ $t('admin_order_alarm.add') }} <BaseIcon icon="plus" class="ml-1" /></button>
            <!-- answers written before the structured fields existed -->
            <p v-if="order_alarm.customer_info.email_sms_detail" class="text-xs italic text-gray-500 mt-2">
              {{ $t('admin_order_alarm.legacy_free_text') }}: {{ order_alarm.customer_info.email_sms_detail }}
            </p>
          </div>
          <div class="w-full px-2 pt-2 pb-1">
            <label class="inline-flex items-center text-sm text-gray-800 cursor-pointer">
              <input type="checkbox" class="form-checkbox h-4 w-4" v-model="order_alarm.notify_individual" />
              <span class="ml-2 font-semibold">{{ $t('admin_order_alarm.notifications_individual') }}</span>
            </label>
            <p class="text-xs italic text-gray-500 ml-6">{{ $t('admin_order_alarm.notifications_individual_desc') }}</p>
          </div>
          <div class="w-full px-2 py-2" v-if="order_alarm.notify_individual">
            <BaseTextarea rows="5" v-model="order_alarm.notification_individual_detail" :field_name="$t('admin_order_alarm.notifications_individual')" />
            <p class="text-xs italic text-gray-500 mt-1">{{ $t('admin_order_alarm.notifications_individual_hint') }}</p>
          </div>
        </div>

        <div class="w-full mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap mt-4">
          <div class="w-full text-gray-600 text-md font-bold px-2 pb-3 font-serif">
            {{ $t('admin_order_alarm.alarm_basis') }}
          </div>
          <div class="w-full md:w-1/2 px-2 py-2">
            <ValidationProvider :name="$t('admin_order_alarm.total_users')" rules="required|numeric" v-slot="{ classes, errors }">
              <div class="input-validate" :class="classes">
                <label class="text-gray-600 text-xs pb-2 font-serif">{{ $t('admin_order_alarm.total_users') }}<b class="text-red-500 ml-1">*</b></label>
                <input type="text" v-model="order_alarm.customer_info.total_user" @keypress="isNumber($event)" class="bg-gray-100 rounded w-full text-sm text-gray-900 focus:outline-none border-b-2 border-gray-300 transition duration-500 px-3 pt-1 pb-1" />
                <span>{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>
          <div class="w-full md:w-1/2 px-2 py-2">
            <ValidationProvider :name="$t('admin_order_alarm.work_tasks')" rules="required" v-slot="{ classes, errors }">
              <div class="input-validate" :class="classes">
                <label class="text-gray-600 text-xs pb-2 font-serif">{{ $t('admin_order_alarm.work_tasks') }}<b class="text-red-500 ml-1">*</b></label>
                <textarea rows="4" v-model="order_alarm.customer_info.work_task" class="bg-gray-100 rounded w-full text-sm text-gray-900 focus:outline-none border-b-2 border-gray-300 transition duration-500 px-3 pt-1 pb-1"></textarea>
                <span>{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>
          <div class="w-full md:w-1/2 px-2 py-2">
            <ValidationProvider :name="$t('admin_order_alarm.workplace_location')" rules="required" v-slot="{ classes, errors }">
              <div class="input-validate" :class="classes">
                <label class="text-gray-600 text-xs pb-2 font-serif">{{ $t('admin_order_alarm.workplace_location') }}<b class="text-red-500 ml-1">*</b></label>
                <input type="text" v-model="order_alarm.customer_info.working_place" class="bg-gray-100 rounded w-full text-sm text-gray-900 focus:outline-none border-b-2 border-gray-300 transition duration-500 px-3 pt-1 pb-1" />
                <span>{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>
          <div class="w-full md:w-1/2 px-2 py-2">
            <ValidationProvider :name="$t('admin_order_alarm.risk_assessment')" rules="required" v-slot="{ classes, errors }">
              <div class="input-validate" :class="classes">
                <label class="text-gray-600 text-xs pb-2 font-serif">{{ $t('admin_order_alarm.risk_assessment') }}<b class="text-red-500 ml-1">*</b></label>
                <input type="text" v-model="order_alarm.customer_info.risk_detail" class="bg-gray-100 rounded w-full text-sm text-gray-900 focus:outline-none border-b-2 border-gray-300 transition duration-500 px-3 pt-1 pb-1" />
                <span>{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>
          <div class="w-full md:w-1/2 px-2 py-2">
            <ValidationProvider name="" rules="" v-slot="{ classes, errors }">
              <div class="input-validate" :class="classes">
                <label class="text-gray-600 text-xs pb-2 font-serif">{{ $t('admin_order_alarm.other_info') }}</label>
                <textarea rows="3" v-model="order_alarm.customer_info.other_info" class="bg-gray-100 rounded w-full text-sm text-gray-900 focus:outline-none border-b-4 border-gray-300 transition duration-500 px-3 pt-2 pb-2 disabled:opacity-50" />
                <span>{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>
        </div>

        <div class="w-full mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap mt-4">
          <div class="w-full md:w-1/3 px-2 py-2">

            <BaseSelect v-model="order_alarm.seller" :options="orderSellers" nameSelector="name" optionSelector="_id" :field_name="$t('admin_order_alarm.seller')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseSelect v-model="order_alarm.sim_card" :options="sim_operators" :field_name="$t('admin_order_alarm.sim_card')" rules="required" />
          </div>
        </div>
        <div class="flex flex-wrap mt-4">
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseSelect v-model="order_alarm.alarm_center" :options="[{ name: $t('admin_order_alarm.none'), _id: '-' }, ...orderAlarmCenters]" nameSelector="name" optionSelector="_id" :field_name="$t('admin_order_alarm.alarm_center')" rules="required" @change="onAlarmCenterChange" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseSwitch v-model="order_alarm.direct_dispatch" :field_name="$t('alarm_pdf.direct_dispatch')" />
          </div>
        </div>
        <!-- demo feedback: with guard dispatch on, the external contractor is
             captured like on the create-device page and inherited into the
             devices' external_contractor for the action report -->
        <div class="flex flex-wrap" v-if="order_alarm.direct_dispatch">
          <div class="w-full text-xs italic px-2 mb-1">
            {{ $t('alarm_create.external_contractor') }}
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="order_alarm.security_company" type="text" :field_name="$t('alarm_create.name')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="order_alarm.security_company_phone" type="text" :field_name="$t('alarm_create.contact_number')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="order_alarm.security_company_object_id" type="text" :field_name="$t('alarm_create.object_id')" />
          </div>
        </div>
  
        <!-- demo feedback: plain checkboxes, unchecked by default, with a note
             that the choices drive the alarm configuration -->
        <div class="flex flex-wrap mt-4">
          <div class="w-full text-xs italic text-gray-500 px-2 pb-2">
            {{ $t('admin_order_alarm.alarm_options_hint') }}
          </div>
          <div class="w-full md:w-1/3 px-2 py-2" v-for="option in alarmOptionCheckboxes" :key="option.field">
            <label class="inline-flex items-center text-sm text-gray-800 cursor-pointer">
              <input type="checkbox" class="form-checkbox h-4 w-4" v-model="order_alarm[option.field]" />
              <span class="ml-2 font-bold text-gray-600 font-serif">{{ option.label }}</span>
            </label>
          </div>
        </div>
        <!-- demo feedback: delivery method replaces "ship directly to customer";
             only "sent to recipient" needs an address and a tracking link -->
        <div class="flex flex-wrap mt-4">
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseSelect v-model="order_alarm.delivery_method" :options="deliveryMethodOptions" nameSelector="name" optionSelector="value" :field_name="$t('admin_order_alarm.delivery_method')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2" v-if="isDeliveryToRecipient">
            <BaseInput v-model="order_alarm.direct_shipping_address" type="text" :field_name="$t('admin_order_alarm.direct_shipping_address')" rules="required" />
            <p class="text-xs italic text-gray-500 mt-1">{{ $t('admin_order_alarm.delivery_address_hint') }}</p>
          </div>
          <div class="w-full md:w-1/3 px-2 py-2" v-if="isDeliveryToRecipient">
            <BaseInput v-model="order_alarm.delivery_tracking_link" type="text" :field_name="$t('admin_order_alarm.delivery_tracking_link')" />
            <p class="text-xs italic text-gray-500 mt-1">{{ $t('admin_order_alarm.delivery_tracking_link_hint') }}</p>
          </div>
        </div>



        <div class="w-full mx-2 border-b border-secondary-400 my-5"></div>

        <div class="flex flex-wrap mt-4">
          <div class="w-full text-gray-600 text-sm font-bold px-2 pb-1 font-serif">
            {{ $t('admin_order_alarm.contact_persons') }}
          </div>
          <!-- demo feedback: say who these contact persons are -->
          <div class="w-full text-xs italic text-gray-500 px-2 pb-2">
            {{ $t('admin_order_alarm.contact_persons_hint') }}
          </div>

          <!-- Safe-4 (SEC-364 A4): the contact persons are called around the clock,
               so the time fields are locked. The note sits above them, in italics. -->
          <div class="w-full px-2 text-xs italic mb-1" v-if="isSafe4Selected">
            {{ $t("safe4_form.around_clock") }}
          </div>
          <div class="w-full" v-for="(c, i) in order_alarm.contact_persons" :key="`order-contact-${i}`">
            <div class="flex flex-wrap" v-if="i === 0">
              <div class="w-full md:w-3/12 px-2 py-1">
                <BaseInput v-model="c.name" type="text" :rules="orderContactRules(!!firstContactRules, 50)" :maxlength="isSafe4Selected ? 50 : undefined" :field_name="$t('alarm_create.name')" :placeholder="$t('alarm_create.name')" />
              </div>
              <div class="w-full md:w-3/12 px-2 py-1">
                <BaseInput v-model="c.function" type="text" :rules="orderContactRules(!!firstContactRules, 40)" :maxlength="isSafe4Selected ? 40 : undefined" :field_name="$t('alarm_create.function')" :placeholder="$t('alarm_create.function')" />
              </div>
              <div class="w-full md:w-3/12 px-2 py-1">
                <BasePhoneInput v-model="c.phone" vid="contact-phone-0" :show_country="isSafe4Selected" :rules="safe4PhoneRules(order_alarm.contact_persons, 0, isSafe4Selected, !!firstContactRules)" :maxlength="isSafe4Selected ? 20 : undefined" :field_name="$t('alarm_create.number')" :placeholder="$t('alarm_create.number')" />
              </div>
              <div class="w-full md:w-1/12 px-2 py-1">
                <BaseInput v-model="c.weekday" type="text" :disabled="isSafe4Selected" :field_name="$t('alarm_create.weekday')" :placeholder="$t('alarm_create.weekday')" />
              </div>
              <div class="w-full md:w-1/12 px-2 py-1">
                <BaseInput v-model="c.saturday" type="text" :disabled="isSafe4Selected" :field_name="$t('alarm_create.saturday')" :placeholder="$t('alarm_create.saturday')" />
              </div>
              <div class="w-full md:w-1/12 px-2 py-1">
                <BaseInput v-model="c.sunday" type="text" :disabled="isSafe4Selected" :field_name="$t('alarm_create.sunday_holiday')" :placeholder="$t('alarm_create.sunday_holiday')" />
              </div>
            </div>
            <div class="flex flex-wrap" v-else>
              <div class="w-full md:w-3/12 px-2 py-1">
                <BaseInput v-model="c.name" type="text" :rules="orderContactRules(false, 50)" :maxlength="isSafe4Selected ? 50 : undefined" :placeholder="$t('alarm_create.name')" />
              </div>
              <div class="w-full md:w-3/12 px-2 py-1">
                <BaseInput v-model="c.function" type="text" :rules="orderContactRules(false, 40)" :maxlength="isSafe4Selected ? 40 : undefined" :placeholder="$t('alarm_create.function')" />
              </div>
              <div class="w-full md:w-3/12 px-2 py-1">
                <BasePhoneInput v-model="c.phone" :vid="'contact-phone-' + i" :show_country="isSafe4Selected" :rules="safe4PhoneRules(order_alarm.contact_persons, i, isSafe4Selected, false)" :maxlength="isSafe4Selected ? 20 : undefined" :placeholder="$t('alarm_create.number')" />
              </div>
              <div class="w-full md:w-1/12 px-2 py-1">
                <BaseInput v-model="c.weekday" type="text" :disabled="isSafe4Selected" :placeholder="$t('alarm_create.weekday')" />
              </div>
              <div class="w-full md:w-1/12 px-2 py-1">
                <BaseInput v-model="c.saturday" type="text" :disabled="isSafe4Selected" :placeholder="$t('alarm_create.saturday')" />
              </div>
              <div class="w-full md:w-1/12 px-2 py-1">
                <BaseInput v-model="c.sunday" type="text" :disabled="isSafe4Selected" :placeholder="$t('alarm_create.sunday_holiday')" />
              </div>
            </div>
          </div>
        </div>

        <!-- SEC-221 REQ-03: alarm-holder user account — always created as a
             department-level user (role 'user') when the order is placed.
             SEC-352: one row of fields per account, the plus button adds a row -->
        <div class="w-full mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap mt-4">
          <div class="w-full text-gray-600 text-md font-bold px-2 pb-1 font-serif">
            {{ $t('admin_order_alarm.user_account_title') }}
          </div>

          <div class="w-full px-2 pb-2">
            <label class="mr-6 text-sm text-gray-800 cursor-pointer">
              <input type="radio" class="mr-1" name="account-wanted" :value="true" v-model="accountWanted" :disabled="accountLocked" />
              {{ $t('yes') }}
            </label>
            <label class="text-sm text-gray-800 cursor-pointer">
              <input type="radio" class="mr-1" name="account-wanted" :value="false" v-model="accountWanted" :disabled="accountLocked" />
              {{ $t('no') }}
            </label>
          </div>
          <template v-if="accountWanted">
            <div class="w-full" v-for="(account, i) in order_alarm.user_accounts" :key="`account-${i}`">
              <!-- the outcome once the order is placed -->
              <div class="w-full px-2 pt-2 text-sm text-green-700 font-semibold" v-if="account.status === 'created'">
                {{ $t('admin_order_alarm.account_created', { username: account.username }) }}
              </div>
              <div class="w-full px-2 pt-2 text-sm text-red-600 font-semibold" v-if="account.status === 'failed'">
                {{ accountFailureMessage(account) }}
              </div>

              <!-- demo feedback: no .trim on the names or the number - it strips
                   the space as it is typed, so "Anna Maria", "van der Berg" and
                   "070-123 45 67" could not be entered. The server trims them. -->
              <div class="w-full flex items-start">
                <div class="flex-grow flex flex-wrap">
                  <div class="w-full md:w-1/4 px-2 py-2">
                    <BaseInput v-model="account.firstname" :vid="`account-firstname-${i}`" type="text" :field_name="$t('create_user_page.first_name')" rules="required" :readonly="accountLocked" :disabled="accountLocked" />
                  </div>
                  <div class="w-full md:w-1/4 px-2 py-2">
                    <BaseInput v-model="account.lastname" :vid="`account-lastname-${i}`" type="text" :field_name="$t('create_user_page.surname')" rules="required" :readonly="accountLocked" :disabled="accountLocked" />
                  </div>
                  <div class="w-full md:w-1/4 px-2 py-2">
                    <BaseInput v-model.trim="account.email" :vid="`account-email-${i}`" type="text" :field_name="$t('create_user_page.email')" :rules="accountEmailRules(i)" :readonly="accountLocked" :disabled="accountLocked" />
                  </div>
                  <div class="w-full md:w-1/4 px-2 py-2">
                    <BaseInput v-model="account.phone" :vid="`account-phone-${i}`" type="text" :field_name="$t('create_user_page.phone')" :readonly="accountLocked" :disabled="accountLocked" />
                  </div>
                </div>
                <button v-if="order_alarm.user_accounts.length > 1 && !accountLocked" class="text-red-500 px-2 mt-10" @click.prevent="removeAccountRow(i)">
                  <BaseIcon icon="minus-circle" />
                </button>
              </div>
            </div>

            <div class="w-full px-2 py-2" v-if="!accountLocked">
              <button class="btn-blue-outline px-3 py-2" @click.prevent="addAccountRow()">
                {{ $t('admin_order_alarm.account_add') }} <BaseIcon icon="plus" class="ml-1" />
              </button>
            </div>
          </template>
        </div>

        <!-- demo feedback: one comment for the whole order, right after the
             account it belongs with -->
        <div class="flex flex-wrap mt-4">
          <div class="w-full px-2 py-2">
            <BaseTextarea rows="5" v-model="order_alarm.comment" :field_name="$t('admin_order_alarm.comment_order_handling')" />
          </div>
        </div>

        <div class="w-full mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap mt-4">
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseSelect v-model="order_alarm.agreement_category" :options="agreementCategoryOptions" nameSelector="name" optionSelector="value" :field_name="$t('admin_order_alarm.agreement_category')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseSelect v-model="order_alarm.insurance" :options="insuranceOptions" nameSelector="name" optionSelector="value" :field_name="$t('admin_order_alarm.insurance')" rules="required" />
          </div>
        </div>

        <div class="w-full mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap">
          <div class="w-full px-2">
            <div class="flex flex-col">
              <div class="w-full text-gray-600 text-sm font-bold pb-3 font-serif">
                {{ $t('admin_order_alarm.product') }}
              </div>
              
              <!-- Hierarchical Category/Subcategory/Product Display -->
              <div class="space-y-2">
                <div v-for="category in categoriesWithProducts" :key="category.value" class="border border-gray-200 rounded">
                  <!-- Category Header -->
                  <div 
                    class="px-4 py-2 bg-gray-50 cursor-pointer flex justify-between items-center hover:bg-gray-100"
                    @click="toggleCategory(category.value)"
                  >
                    <div class="flex items-center">
                      <span class="text-sm font-semibold text-gray-800">{{ category.label }}</span>
                      <span class="ml-2 text-xs text-gray-500">({{ groupedProducts[category.value].length }})</span>
                    </div>
                    <span class="text-gray-500 text-xs">{{ expandedCategories[category.value] ? '▲' : '▼' }}</span>
                  </div>
                  
                  <!-- Category Content -->
                  <div v-show="expandedCategories[category.value]" class="bg-white">
                    
                    <!-- Subcategories -->
                    <div v-if="subcategoriesByParent[category.value] && subcategoriesByParent[category.value].length > 0">
                      <div v-for="subcategory in subcategoriesByParent[category.value]" :key="subcategory._id" class="border-b border-gray-100">
                        <!-- Subcategory Header -->
                        <div 
                          class="px-4 py-2 pl-8 bg-blue-50 cursor-pointer flex justify-between items-center hover:bg-blue-100"
                          @click="toggleSubcategory(subcategory._id)"
                        >
                          <div class="flex items-center">
                            <span class="text-sm font-medium text-blue-800">{{ subcategory.displayName }}</span>
                            <span class="ml-2 text-xs text-blue-600">({{ getProductsForSubcategory(category.value, subcategory._id).length }})</span>
                          </div>
                          <span class="text-blue-500 text-xs">{{ expandedSubcategories[subcategory._id] ? '▲' : '▼' }}</span>
                        </div>
                        
                        <!-- Subcategory Products -->
                        <div v-show="expandedSubcategories[subcategory._id]" class="pl-8 pr-2 py-2 space-y-2">
                          <template v-for="p in getProductsForSubcategory(category.value, subcategory._id)">
                            <!-- Product Row -->
                            <div :key="p._id || p.type" class="flex flex-wrap items-center bg-gray-50 rounded p-2">
                              <div class="w-2/5 text-sm flex items-center">
                                <button v-if="p.hasVariants && p.variants && p.variants.length > 0" @click.stop="toggleVariants(p._id)" class="mr-2 text-gray-500 hover:text-gray-700 text-xs">
                                  {{ expandedVariants[p._id] ? '▼' : '▶' }}
                                </button>
                                <div class="flex-1">
                                  <template v-if="p.display_name">
                                    <div class="font-semibold">{{ p.display_name }}</div>
                                    <div class="text-xs text-gray-500">{{ p.name }}</div>
                                  </template>
                                  <template v-else>
                                    <span class="font-medium">{{ p.name }}</span>
                                  </template>
                                  <div v-if="p.hasVariants && p.variants && p.variants.length > 0" class="text-xs text-blue-600">{{ p.variants.length }} varianter</div>
                                </div>
                              </div>
                              <template v-if="p.variants && p.variants.length > 0">
                                <div class="w-3/5 px-2 text-xs text-gray-500 italic">{{ $t('admin_order_alarm.select_variant_to_order') }}</div>
                              </template>
                              <template v-else>
                                <div class="w-1/5 px-2">
                                  <input type="number" v-model.number="p.qty" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" placeholder="Antal" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" />
                                </div>
                                <div class="w-1/5 px-2" v-if="shouldShowRentalForProduct(p)">
                                  <input type="number" v-model.number="p.price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="p.recommended_rental_price > 0 ? 'Rek: ' + p.recommended_rental_price : 'Hyra'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" />
                                </div>
                                <div class="w-1/5 px-2" v-if="shouldShowCashForProduct(p)">
                                  <input type="number" v-model.number="p.cash_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="p.recommended_cash_price > 0 ? 'Rek: ' + p.recommended_cash_price : 'Kontant'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" />
                                </div>
                              </template>
                            </div>
                            <!-- Variant Rows -->
                            <template v-if="expandedVariants[p._id] && p.variants && p.variants.length > 0">
                              <div v-for="variant in p.variants" :key="`${p.type}-${variant._id}`" class="flex flex-wrap items-center bg-blue-50 rounded p-2 ml-6 border-l-2 border-blue-300">
                                <div class="w-2/5 text-sm pl-2 flex items-center">
                                  <span class="text-blue-500 mr-2">↳</span>
                                  <div>
                                    <span class="text-gray-700 font-medium">{{ variant.name }}</span>
                                    <div class="text-xs text-gray-500">{{ variant.articleNumber || '-' }}</div>
                                  </div>
                                </div>
                                <div class="w-1/5 px-2">
                                  <input type="number" v-model.number="variant.qty" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" placeholder="Antal" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" />
                                </div>
                                <div class="w-1/5 px-2" v-if="shouldShowRentalForProduct(p)">
                                  <input type="number" v-model.number="variant.price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="variant.recommended_rental_price > 0 ? 'Rek: ' + variant.recommended_rental_price : 'Hyra'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" />
                                </div>
                                <div class="w-1/5 px-2" v-if="shouldShowCashForProduct(p)">
                                  <input type="number" v-model.number="variant.cash_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="variant.recommended_cash_price > 0 ? 'Rek: ' + variant.recommended_cash_price : 'Kontant'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" />
                                </div>
                              </div>
                            </template>
                          </template>
                        </div>
                      </div>
                      
                      <!-- Products Without Subcategory -->
                      <div v-if="getProductsWithoutSubcategory(category.value).length > 0" class="border-b border-gray-100">
                        <div 
                          class="px-4 py-2 pl-8 bg-gray-100 cursor-pointer flex justify-between items-center hover:bg-gray-200"
                          @click="toggleSubcategory(`${category.value}-none`)"
                        >
                          <div class="flex items-center">
                            <span class="text-sm font-medium text-gray-600">{{ $t('products.without_subcategory') }}</span>
                            <span class="ml-2 text-xs text-gray-500">({{ getProductsWithoutSubcategory(category.value).length }})</span>
                          </div>
                          <span class="text-gray-500 text-xs">{{ expandedSubcategories[`${category.value}-none`] ? '▲' : '▼' }}</span>
                        </div>
                        
                        <div v-show="expandedSubcategories[`${category.value}-none`]" class="pl-8 pr-2 py-2 space-y-2">
                          <template v-for="p in getProductsWithoutSubcategory(category.value)">
                            <div :key="p._id || p.type" class="flex flex-wrap items-center bg-gray-50 rounded p-2">
                              <div class="w-2/5 text-sm flex items-center">
                                <button v-if="p.hasVariants && p.variants && p.variants.length > 0" @click.stop="toggleVariants(p._id)" class="mr-2 text-gray-500 hover:text-gray-700 text-xs">
                                  {{ expandedVariants[p._id] ? '▼' : '▶' }}
                                </button>
                                <div class="flex-1">
                                  <template v-if="p.display_name">
                                    <div class="font-semibold">{{ p.display_name }}</div>
                                    <div class="text-xs text-gray-500">{{ p.name }}</div>
                                  </template>
                                  <template v-else>
                                    <span class="font-medium">{{ p.name }}</span>
                                  </template>
                                  <div v-if="p.hasVariants && p.variants && p.variants.length > 0" class="text-xs text-blue-600">{{ p.variants.length }} varianter</div>
                                </div>
                              </div>
                              <template v-if="p.variants && p.variants.length > 0">
                                <div class="w-3/5 px-2 text-xs text-gray-500 italic">{{ $t('admin_order_alarm.select_variant_to_order') }}</div>
                              </template>
                              <template v-else>
                                <div class="w-1/5 px-2">
                                  <input type="number" v-model.number="p.qty" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" placeholder="Antal" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" />
                                </div>
                                <div class="w-1/5 px-2" v-if="shouldShowRentalForProduct(p)">
                                  <input type="number" v-model.number="p.price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="p.recommended_rental_price > 0 ? 'Rek: ' + p.recommended_rental_price : 'Hyra'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" />
                                </div>
                                <div class="w-1/5 px-2" v-if="shouldShowCashForProduct(p)">
                                  <input type="number" v-model.number="p.cash_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="p.recommended_cash_price > 0 ? 'Rek: ' + p.recommended_cash_price : 'Kontant'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" />
                                </div>
                              </template>
                            </div>
                            <template v-if="expandedVariants[p._id] && p.variants && p.variants.length > 0">
                              <div v-for="variant in p.variants" :key="`${p.type}-${variant._id}`" class="flex flex-wrap items-center bg-blue-50 rounded p-2 ml-6 border-l-2 border-blue-300">
                                <div class="w-2/5 text-sm pl-2 flex items-center">
                                  <span class="text-blue-500 mr-2">↳</span>
                                  <div>
                                    <span class="text-gray-700 font-medium">{{ variant.name }}</span>
                                    <div class="text-xs text-gray-500">{{ variant.articleNumber || '-' }}</div>
                                  </div>
                                </div>
                                <div class="w-1/5 px-2">
                                  <input type="number" v-model.number="variant.qty" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" placeholder="Antal" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" />
                                </div>
                                <div class="w-1/5 px-2" v-if="shouldShowRentalForProduct(p)">
                                  <input type="number" v-model.number="variant.price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="variant.recommended_rental_price > 0 ? 'Rek: ' + variant.recommended_rental_price : 'Hyra'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" />
                                </div>
                                <div class="w-1/5 px-2" v-if="shouldShowCashForProduct(p)">
                                  <input type="number" v-model.number="variant.cash_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="variant.recommended_cash_price > 0 ? 'Rek: ' + variant.recommended_cash_price : 'Kontant'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" />
                                </div>
                              </div>
                            </template>
                          </template>
                        </div>
                      </div>
                    </div>
                    
                    <!-- No Subcategories: Show products directly -->
                    <div v-else class="px-4 py-2 space-y-2">
                      <template v-for="p in groupedProducts[category.value]">
                        <div :key="p._id || p.type" class="flex flex-wrap items-center bg-gray-50 rounded p-2">
                          <div class="w-2/5 text-sm flex items-center">
                            <button v-if="p.hasVariants && p.variants && p.variants.length > 0" @click.stop="toggleVariants(p._id)" class="mr-2 text-gray-500 hover:text-gray-700 text-xs">
                              {{ expandedVariants[p._id] ? '▼' : '▶' }}
                            </button>
                            <div class="flex-1">
                              <template v-if="p.display_name">
                                <div class="font-semibold">{{ p.display_name }}</div>
                                <div class="text-xs text-gray-500">{{ p.name }}</div>
                              </template>
                              <template v-else>
                                <span class="font-medium">{{ p.name }}</span>
                              </template>
                              <div v-if="p.hasVariants && p.variants && p.variants.length > 0" class="text-xs text-blue-600">{{ p.variants.length }} varianter</div>
                            </div>
                          </div>
                          <template v-if="p.variants && p.variants.length > 0">
                            <div class="w-3/5 px-2 text-xs text-gray-500 italic">{{ $t('admin_order_alarm.select_variant_to_order') }}</div>
                          </template>
                          <template v-else>
                            <div class="w-1/5 px-2">
                              <input type="number" v-model.number="p.qty" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" placeholder="Antal" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" />
                            </div>
                            <div class="w-1/5 px-2" v-if="shouldShowRentalForProduct(p)">
                              <input type="number" v-model.number="p.price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="p.recommended_rental_price > 0 ? 'Rek: ' + p.recommended_rental_price : 'Hyra'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" />
                            </div>
                            <div class="w-1/5 px-2" v-if="shouldShowCashForProduct(p)">
                              <input type="number" v-model.number="p.cash_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="p.recommended_cash_price > 0 ? 'Rek: ' + p.recommended_cash_price : 'Kontant'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" />
                            </div>
                          </template>
                        </div>
                        <template v-if="expandedVariants[p._id] && p.variants && p.variants.length > 0">
                          <div v-for="variant in p.variants" :key="`${p.type}-${variant._id}`" class="flex flex-wrap items-center bg-blue-50 rounded p-2 ml-6 border-l-2 border-blue-300">
                            <div class="w-2/5 text-sm pl-2 flex items-center">
                              <span class="text-blue-500 mr-2">↳</span>
                              <div>
                                <span class="text-gray-700 font-medium">{{ variant.name }}</span>
                                <div class="text-xs text-gray-500">{{ variant.articleNumber || '-' }}</div>
                              </div>
                            </div>
                            <div class="w-1/5 px-2">
                              <input type="number" v-model.number="variant.qty" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" placeholder="Antal" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" />
                            </div>
                            <div class="w-1/5 px-2" v-if="shouldShowRentalForProduct(p)">
                              <input type="number" v-model.number="variant.price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="variant.recommended_rental_price > 0 ? 'Rek: ' + variant.recommended_rental_price : 'Hyra'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" />
                            </div>
                            <div class="w-1/5 px-2" v-if="shouldShowCashForProduct(p)">
                              <input type="number" v-model.number="variant.cash_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="variant.recommended_cash_price > 0 ? 'Rek: ' + variant.recommended_cash_price : 'Kontant'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" />
                            </div>
                          </div>
                        </template>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="w-full mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap">
          <div class="w-full md:w-1/3 px-2 py-2" v-if="order_alarm.agreement_category === 'cash_services'">
            <BaseInput v-model.number="totalCashPrice" @keypress="isNumber($event)" type="text" :field_name="$t('admin_order_alarm.total_cash_price')" disabled />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2" v-if="order_alarm.agreement_category === 'rental' || order_alarm.agreement_category === 'financed'">
            <BaseInput v-model.number="cashSumForRentalFinanced" @keypress="isNumber($event)" type="text" :field_name="$t('admin_order_alarm.cash_sum')" disabled />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseSelect v-model="order_alarm.contract_period" :options="contractPeriodOptions" nameSelector="name" optionSelector="value" rules="required" :field_name="$t('admin_order_alarm.contract_period')" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2" v-if="isContractPeriodFreetext">
            <BaseInput v-model.number="order_alarm.contract_period_freetext" @keypress="isNumber($event)" type="text" :field_name="$t('admin_order_alarm.contract_period_freetext_label')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model.number="order_alarm.setup_fee" @keypress="isNumber($event)" type="text" :field_name="$t('admin_order_alarm.setup_fee')" :placeholder="String(total_alarm_center_devices * 995)" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2" v-if="showRentalPrice">
            <BaseInput v-model.number="order_alarm.rental_month_cost" @keypress="isNumber($event)" type="text" :field_name="$t('admin_order_alarm.rental_cost')" rules="required" disabled />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseSelect v-model="order_alarm.payment_terms" :options="paymentTermsOptions" nameSelector="name" optionSelector="value" rules="required" :field_name="$t('admin_order_alarm.payment_terms')" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2" v-if="isPaymentTermsFreetext">
            <BaseInput v-model="order_alarm.payment_terms_freetext" type="text" :field_name="$t('agreement_create.payment_terms_freetext_label')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <ValidationProvider :name="$t('admin_order_alarm.contract_start')" rules="required" v-slot="{ classes, errors }">
              <div class="input-validate" :class="classes">
                <label class="text-gray-600 text-sm font-bold pb-2 font-serif">{{ $t('admin_order_alarm.contract_start') }}<b class="text-red-500 ml-1">*</b></label>
                <date-picker :lang="date_picker_lang" :key="date_picker_langKey" class="mt-2" v-model="order_alarm.contract_start" value-type="format" format="YYYY-MM-DD" :editable="false" :clearable="false"></date-picker>
                <span>{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>
          <div class="w-full px-2 py-2">
            <BaseTextarea rows="4" v-model="order_alarm.special_terms" :field_name="$t('admin_order_alarm.special_terms')" rules="required" />
          </div>
        </div>

        <!-- demo feedback: the alarm plan of the order (personal/group,
             risk profile and the personal details) sits after the terms -->
        <div class="w-full mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap mt-4">
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseSelect v-model="order_alarm.selected_alarm_sort" :options="safe4AlarmSortChoices(alarmSortOptions, isSafe4Selected)" nameSelector="label" optionSelector="value" rules="required" :field_name="$t('alarm_create.personal_group_alarm')" />
          </div>
          <!-- a personal order answers these per person, in the blocks below -->
          <div class="w-full md:w-1/3 px-2 py-2" v-if="!isPersonalAlarm">
            <BaseSelect v-model="order_alarm.risk_profile" :options="riskProfileOptions" nameSelector="label" optionSelector="value" rules="required" :field_name="$t('admin_order_alarm.risk_profile')" v-if="!showMalmoRiskProfiles" />
            <BaseSelect v-model="order_alarm.risk_profile" :options="riskProfileOptionsMSL" nameSelector="label" optionSelector="value" rules="required" :field_name="$t('admin_order_alarm.risk_profile')" v-if="showMalmoRiskProfiles" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2" v-if="!isPersonalAlarm && !isSafe4Selected">
            <BaseInput v-model="order_alarm.from_address" type="text" rules="required" :field_name="$t('admin_order_alarm.from_address')" />
          </div>
        </div>
        <!-- Safe-4 (SEC-370): the address is split into street / postal code / city,
             like on the device forms, so the devices created from the order carry
             a postcode Safe-4 accepts. -->
        <div class="flex flex-wrap" v-if="isSafe4Selected && !isPersonalAlarm">
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="order_alarm.from_address_parts.street" type="text" rules="required|max:30" maxlength="30" :field_name="$t('safe4_form.street')" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="order_alarm.from_address_parts.zip" type="text" :rules="safe4PostcodeRules" :field_name="$t('safe4_form.postal_code')" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="order_alarm.from_address_parts.city" type="text" rules="required" :field_name="$t('safe4_form.city')" />
          </div>
        </div>

        <!-- demo feedback: the case reference number, asked for the same risk
             profiles as on the create-device page, with the description above
             it. Inherited by the devices created from the order. -->
        <div class="flex flex-wrap" v-if="riskProfileCode === 'threat' || riskProfileCode === 'assault'">
          <div class="w-full md:w-1/3 px-2 py-2">
            <p class="text-xs italic text-gray-500 mb-1">{{ $t('admin_order_alarm.reference_number_hint') }}</p>
            <BaseInput v-model="order_alarm.reference_number" type="text" :field_name="$t('admin_order_alarm.reference_number')" />
          </div>
        </div>

        <!-- demo feedback: a personal alarm asks for the same details as the
             create-device page, one block per person - up to the number of
             alarm devices the order carries. Which device ends up with which
             person is decided when the devices are created; support can move
             them afterwards. The product name comes below them. -->
        <template v-if="isPersonalAlarm">
          <div class="w-full px-2 pt-2 flex items-center justify-between">
            <span class="text-gray-600 text-sm font-bold font-serif">{{ $t('admin_order_alarm.personal_details') }}</span>
            <span class="text-xs text-gray-500">{{ $t('admin_order_alarm.personal_details_count', { used: personalAlarmBlocks.length, total: maxPersonalAlarmBlocks }) }}</span>
          </div>
          <p class="w-full px-2 pb-2 text-xs italic text-gray-500">{{ $t('admin_order_alarm.personal_details_hint') }}</p>

          <div class="w-full px-2 pb-3" v-for="(person, i) in personalAlarmBlocks" :key="`personal-${i}`">
            <div class="border border-gray-200 rounded">
              <!-- header: person number, and a way to fold it away or remove it -->
              <div class="flex items-center justify-between bg-gray-50 px-3 py-2 cursor-pointer" @click="togglePersonalBlock(i)">
                <span class="text-sm font-semibold text-gray-800">
                  {{ $t('admin_order_alarm.personal_person', { n: i + 1 }) }}
                  <span class="text-xs font-normal text-gray-500 ml-2" v-if="person.name">— {{ person.name }}</span>
                </span>
                <span class="flex items-center">
                  <button v-if="personalAlarmBlocks.length > 1" class="text-red-500 mr-3" @click.stop.prevent="removePersonalBlock(i)">
                    <BaseIcon icon="minus-circle" />
                  </button>
                  <span class="text-gray-500 text-xs">{{ collapsedPersonalBlocks[i] ? '▼' : '▲' }}</span>
                </span>
              </div>

              <div class="flex flex-wrap p-2" v-show="!collapsedPersonalBlocks[i]">
                <div class="w-full md:w-1/3 px-2 py-2">
                  <div class="flex items-end">
                    <div class="flex-grow">
                      <BaseSelect v-model="person.risk_profile" :options="riskProfileOptions" :vid="`person-risk-${i}`" nameSelector="label" optionSelector="value" rules="required" :field_name="$t('admin_order_alarm.risk_profile')" v-if="!showMalmoRiskProfiles" />
                      <BaseSelect v-model="person.risk_profile" :options="riskProfileOptionsMSL" :vid="`person-risk-msl-${i}`" nameSelector="label" optionSelector="value" rules="required" :field_name="$t('admin_order_alarm.risk_profile')" v-if="showMalmoRiskProfiles" />
                    </div>
                    <button
                      v-if="personalAlarmBlocks.length > 1"
                      type="button"
                      class="ml-1 mb-3 px-2 py-1.5 text-blue-500 border border-blue-300 rounded hover:bg-blue-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      :disabled="!person.risk_profile"
                      :title="$t('order_devices.apply_to_all')"
                      @click.prevent="applyPersonFieldToAll('risk_profile', i)"
                    >
                      <BaseIcon icon="copy" />
                    </button>
                  </div>
                </div>
                <div class="w-full md:w-1/3 px-2 py-2">
                  <div class="flex items-end">
                    <div class="flex-grow">
                      <BaseInput v-model="person.from_address" :vid="`person-from-${i}`" type="text" rules="required" :field_name="$t('admin_order_alarm.from_address')" />
                    </div>
                    <button
                      v-if="personalAlarmBlocks.length > 1"
                      type="button"
                      class="ml-1 mb-3 px-2 py-1.5 text-blue-500 border border-blue-300 rounded hover:bg-blue-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      :disabled="!(person.from_address || '').trim()"
                      :title="$t('order_devices.apply_to_all')"
                      @click.prevent="applyPersonFieldToAll('from_address', i)"
                    >
                      <BaseIcon icon="copy" />
                    </button>
                  </div>
                </div>
                <div class="w-full md:w-1/3 px-2 py-2">
                  <BaseInput v-model="person.reporting" :vid="`person-reporting-${i}`" type="text" :field_name="$t('alarm_create.reporting')" rules="required" />
                </div>
                <div class="w-full md:w-1/3 px-2 py-2">
                  <BaseInput v-model="person.name" :vid="`person-name-${i}`" type="text" :field_name="$t('alarm_create.name')" rules="required" />
                </div>
                <div class="w-full md:w-1/3 px-2 py-2">
                  <BaseInput v-model="person.home_address" :vid="`person-home-${i}`" type="text" :field_name="$t('alarm_create.home_address')" rules="required" />
                </div>
                <div class="w-full md:w-1/3 px-2 py-2">
                  <BaseSelect v-model="person.transport" :vid="`person-transport-${i}`" :options="transportOptions" nameSelector="label" optionSelector="value" :field_name="$t('alarm_create.transport')" />
                </div>
                <div class="w-full md:w-1/3 px-2 py-2" v-if="person.transport === 'car'">
                  <BaseInput v-model="person.registration_number" :vid="`person-reg-${i}`" type="text" :field_name="$t('alarm_create.registration_number')" rules="required" />
                </div>
                <div class="w-full md:w-1/3 px-2 py-2" v-if="person.transport === 'car'">
                  <BaseInput v-model="person.model" :vid="`person-model-${i}`" type="text" :field_name="$t('alarm_create.model')" rules="required" />
                </div>
                <div class="w-full md:w-1/3 px-2 py-2" v-if="person.transport === 'car'">
                  <BaseInput v-model="person.color" :vid="`person-color-${i}`" type="text" :field_name="$t('alarm_create.color')" rules="required" />
                </div>
                <div class="w-full px-2 py-2">
                  <BaseTextarea rows="2" v-model="person.alarms_name" :vid="`person-names-${i}`" :field_name="$t('admin_order_alarm.product_names')" rules="required" />
                </div>
                <div class="w-full md:w-1/2 px-2 py-2">
                  <BaseInput v-model="person.alarm_password" :vid="`person-password-${i}`" type="text" :field_name="$t('admin_order_alarm.password')" rules="required" maxlength="200" />
                  <p class="text-xs italic text-gray-500 mt-1">{{ $t('admin_order_alarm.password_hint') }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="w-full px-2 pb-3">
            <button v-if="canAddPersonalBlock" class="btn-blue-outline px-3 py-2" @click.prevent="addPersonalBlock()">
              {{ $t('admin_order_alarm.personal_add') }} <BaseIcon icon="plus" class="ml-1" />
            </button>
            <p v-else class="text-xs italic text-gray-500">{{ $t('admin_order_alarm.personal_add_limit', { total: maxPersonalAlarmBlocks }) }}</p>
          </div>
        </template>

        <div class="flex flex-wrap" v-if="!isPersonalAlarm">
          <div class="w-full px-2 py-2">
            <BaseTextarea rows="2" v-model="order_alarm.alarms_name" :field_name="$t('admin_order_alarm.product_names')" rules="required" />
          </div>
        </div>

        <!-- moved down with the rest of the alarm plan; a personal order asks
             for the password per person, in the blocks above -->
        <div class="flex flex-wrap" v-if="!isPersonalAlarm">
          <div class="w-full md:w-1/2 px-2 py-2">
            <BaseInput v-model="order_alarm.alarm_password" vid="alarm-password" type="text" :field_name="$t('admin_order_alarm.password')" :rules="isSafe4Selected ? 'required|max:20|safe4_password' : 'required'" maxlength="200" />
            <p class="text-xs italic text-gray-500 mt-1">{{ $t('admin_order_alarm.password_hint') }}</p>
          </div>
        </div>
        <!-- demo feedback: the same action plan the create-device page shows
             for the chosen risk profile, so the salesperson can confirm with
             the customer what the alarm central will do -->
        <div class="flex flex-wrap mt-2">
          <div class="w-full px-2 text-xs italic" v-if="riskProfileCode === 'threat'">
            <h5 class="font-bold mb-1">{{ $t('alarm_create.risk_threat_primary') }}</h5>
            <p><span class="font-bold">{{ $t('alarm_create.risk_threat_mic_off') }}</span> {{ $t('alarm_create.risk_threat_primary_text') }}</p>
            <h5 class="font-bold mb-1 mt-2">{{ $t('alarm_create.risk_threat_actions') }}</h5>
            <ol>
              <li><span class="font-bold">{{ $t('alarm_create.risk_threat_step_1') }}</span> {{ $t('alarm_create.risk_threat_step_1_text') }}</li>
              <li><span class="font-bold">{{ $t('alarm_create.risk_threat_step_2') }}</span> {{ $t('alarm_create.risk_threat_step_2_text') }}</li>
              <li><span class="font-bold">{{ $t('alarm_create.risk_threat_step_3') }}</span> {{ $t('alarm_create.risk_threat_step_3_text') }}</li>
              <li>{{ $t('alarm_create.risk_threat_step_4') }}</li>
              <li><span class="font-bold">{{ $t('alarm_create.risk_threat_step_5') }}</span> {{ $t('alarm_create.risk_threat_step_5_text') }}</li>
              <li><span class="font-bold">{{ $t('alarm_create.risk_threat_step_6') }}</span> {{ $t('alarm_create.risk_threat_step_6_text') }}</li>
            </ol>
          </div>

          <div class="w-full px-2 text-xs italic" v-if="riskProfileCode === 'emergency'">
            <h5 class="font-bold mb-1">{{ $t('alarm_create.risk_emergency_primary') }}</h5>
            <p><span class="font-bold">{{ $t('alarm_create.risk_emergency_mic_on') }}</span> {{ $t('alarm_create.risk_emergency_primary_text') }}</p>
            <h5 class="font-bold mb-1 mt-2">{{ $t('alarm_create.risk_emergency_actions') }}</h5>
            <ol>
              <li><span class="font-bold">{{ $t('alarm_create.risk_emergency_step_1') }}</span> {{ $t('alarm_create.risk_emergency_step_1_text') }}</li>
              <li><span class="font-bold">{{ $t('alarm_create.risk_emergency_step_2') }}</span> {{ $t('alarm_create.risk_emergency_step_2_text') }}</li>
              <li><span class="font-bold">{{ $t('alarm_create.risk_emergency_step_3') }}</span> {{ $t('alarm_create.risk_emergency_step_3_text') }}</li>
              <li><span class="font-bold">{{ $t('alarm_create.risk_emergency_step_4') }}</span> {{ $t('alarm_create.risk_emergency_step_4_text') }}</li>
            </ol>
          </div>

          <div class="w-full px-2 text-xs italic" v-if="riskProfileCode === 'assault'">
            <h5 class="font-bold mb-1">{{ $t('alarm_create.risk_assault_primary') }}</h5>
            <p><span class="font-bold">{{ $t('alarm_create.risk_assault_mic_off') }}</span> {{ $t('alarm_create.risk_assault_primary_text') }}</p>
            <h5 class="font-bold mb-1 mt-2">{{ $t('alarm_create.risk_assault_actions') }}</h5>
            <ol>
              <li><span class="font-bold">{{ $t('alarm_create.risk_assault_step_1') }}</span> {{ $t('alarm_create.risk_assault_step_1_text') }}</li>
              <li><span class="font-bold">{{ $t('alarm_create.risk_assault_step_2') }}</span> {{ $t('alarm_create.risk_assault_step_2_text') }}</li>
              <li><span class="font-bold">{{ $t('alarm_create.risk_assault_step_3') }}</span> {{ $t('alarm_create.risk_assault_step_3_text') }}</li>
              <li><span class="font-bold">{{ $t('alarm_create.risk_assault_step_4') }}</span> {{ $t('alarm_create.risk_assault_step_4_text') }}</li>
            </ol>
          </div>
        </div>

        <!-- demo feedback: when the order has to be ready is the last thing
             asked, after everything it is asking about -->
        <div class="flex flex-wrap mt-4">
          <div class="w-full md:w-1/2 px-2 py-2">
            <ValidationProvider :name="$t('admin_order_alarm.delivery_deadline')" rules="required" v-slot="{ classes, errors }">
              <div class="input-validate" :class="classes">
                <label class="block text-gray-600 text-sm font-bold pb-2 font-serif">{{ $t('admin_order_alarm.delivery_deadline') }}</label>
                <date-picker :lang="date_picker_lang" :key="date_picker_langKey" class="mt-2" v-model="order_alarm.delivery_period" value-type="format" format="YYYY-MM-DD" :editable="false" :clearable="false"></date-picker>
                <span>{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>
        </div>

        <!-- change log -->
        <template v-if="order_alarm._id">
          <div class="w-full mx-2 border-b border-secondary-400 my-5"></div>
          <div class="flex flex-wrap mt-4 px-2 pb-2">
            <div class="w-full flex justify-between items-center cursor-pointer" @click="showChangeLog = !showChangeLog">
              <span class="text-gray-600 text-md font-bold font-serif">
                {{ $t('order_log.change_log') }} ({{ (order_alarm.change_log || []).length }})
              </span>
              <span class="text-gray-500 text-xs">{{ showChangeLog ? '▲' : '▼' }}</span>
            </div>
            <div v-if="showChangeLog" class="w-full mt-3 max-h-72 overflow-y-auto border border-gray-200 rounded-lg bg-white">
              <p v-if="!(order_alarm.change_log || []).length" class="text-sm text-gray-500 p-4">{{ $t('order_log.change_log_empty') }}</p>
              <div v-for="(entry, i) in sortedChangeLog" :key="`log-${i}`" class="flex items-start px-3 py-3 border-b border-gray-100">
                <span class="flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold mr-3 mt-0.5" :class="logActionClass(entry.action)">
                  {{ logActionLabel(entry.action) }}
                </span>
                <div class="flex-1 min-w-0">
                  <div class="flex justify-between items-baseline">
                    <span class="text-sm text-gray-800 font-semibold">
                      {{ entry.user }}
                      <span v-if="entry.role" class="text-xs text-gray-400 font-normal">({{ entry.role }})</span>
                    </span>
                    <span class="text-xs text-gray-400 ml-2">{{ formatLogDate(entry.created_at) }}</span>
                  </div>
                  <div v-if="logDescription(entry)" class="text-xs text-gray-600 mt-1">{{ logDescription(entry) }}</div>
                  <div v-for="(c, j) in entry.changes" :key="`log-${i}-${j}`" class="text-xs text-gray-600 mt-1">
                    <span class="font-medium text-gray-700">{{ fieldLabel(c.field) }}:</span> {{ formatChange(c) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
      <div class="modal-footer">
        <div class="footer-container clearfix">
          <button v-if="order_alarm.agreement_number == undefined || order_alarm.agreement_number == ''" class="btn-green-outline px-3 py-2 float-left" @click="saveOrder()">{{ $t('admin_order_alarm.save') }}<BaseIcon icon="save" class="ml-1" /></button>
          <button v-if="order_alarm.agreement_number == undefined || order_alarm.agreement_number == ''" class="btn-blue px-3 py-2 float-right" @click="orderNewAlarm()">{{ $t('admin_order_alarm.order') }}<BaseIcon icon="arrow-right" class="ml-1" /></button>
          <button v-if="order_alarm.agreement_number" class="btn-blue px-3 py-2 float-right" @click="updateOrderLog()">{{ $t('admin_order_alarm.update') }}<BaseIcon icon="save" class="ml-1" /></button>
        </div>
      </div>
    </ValidationObserver>
  </modal>
</template>

<script>
const MODAL_WIDTH = 1200;

// Change-log fields whose form label lives under a different locale key
// than the DB field name; fieldLabel() falls back to admin_order_alarm.<field>.
const CHANGE_LOG_FIELD_LABEL_KEYS = {
  assignee: "order_log.assignee",
  delivery_period: "admin_order_alarm.delivery_deadline",
  rental_month_cost: "admin_order_alarm.rental_cost",
  alarms_name: "admin_order_alarm.product_names",
  comment: "admin_order_alarm.comment_order_handling",
  contact_person: "admin_order_alarm.contact_persons",
  alarm_password: "admin_order_alarm.password",
  call_button: "admin_order_alarm.internal_alarm",
  man_down: "admin_order_alarm.man_down",
  timer_alarm: "admin_order_alarm.timer_alarm",
  direct_dispatch: "alarm_pdf.direct_dispatch",
  security_company: "admin_order_alarm.security_company",
  security_company_phone: "alarm_create.contact_number",
  security_company_object_id: "alarm_create.object_id",
  notification_email: "admin_order_alarm.sms_email_question",
  notify_general: "admin_order_alarm.notifications_general",
  notify_individual: "admin_order_alarm.notifications_individual",
  notification_individual_detail: "admin_order_alarm.notifications_individual",
  selected_alarm_sort: "alarm_create.personal_group_alarm",
  stage: "order_log.status",
  agreement_number: "alarm_pdf.agreement_number",
  "customer_info.contact_name": "admin_order_alarm.name",
  "customer_info.contact_email": "admin_order_alarm.email",
  "customer_info.contact_phone": "admin_order_alarm.phone",
  "customer_info.company_name": "admin_order_alarm.company_name",
  "customer_info.unit_name": "alarm_center_menu.unit_name",
  "customer_info.invoice_detail": "admin_order_alarm.invoice_address",
  "customer_info.outdoor_work": "admin_order_alarm.outdoor_work",
  "customer_info.alert_members": "admin_order_alarm.alert_colleagues",
  "customer_info.indoor_tracking": "admin_order_alarm.indoor_location",
  "customer_info.man_down_alert": "admin_order_alarm.man_down",
  "customer_info.email_sms_detail": "admin_order_alarm.sms_email_question",
  "customer_info.total_user": "admin_order_alarm.total_users",
  "customer_info.person_group_names": "admin_order_alarm.person_group_name",
  "customer_info.work_task": "admin_order_alarm.work_tasks",
  "customer_info.working_place": "admin_order_alarm.workplace_location",
  "customer_info.risk_detail": "admin_order_alarm.risk_assessment",
  "customer_info.other_info": "admin_order_alarm.other_info",
};

import DatePicker from "vue2-datepicker";
import "vue2-datepicker/locale/sv";
import "vue2-datepicker/locale/nb";
import "@/utils/datePickerDutch";
import "vue2-datepicker/index.css";
import siteContext from "@/utils/siteContext";
import { splitAddressLine } from "@/utils/safe4Postcodes";
import agreementCostMixin from "@/mixins/agreementCostMixin";
import { RISK_PROFILE, ALARM_SORT, normalizeRiskProfile, normalizeAlarmSort } from "@/utils/alarmEnums";
import { checklistSectionsForProducts } from "@/utils/orderChecklists";
import { paymentTermsOptions as buildPaymentTermsOptions } from "@/utils/paymentTerms";
import AutomaticDeviceCreationPanel from "@/components/order/AutomaticDeviceCreationPanel.vue";

// Safe-4 (SEC-364 A3): contact persons are called around the clock, the same
// value the device forms and the server use
const SAFE4_CONTACT_TIME = "00:00-23:59";

export default {
  name: "adminOrderAlarmModal",
  siteContext,
  mixins: [agreementCostMixin],
  components: {
    DatePicker,
    AutomaticDeviceCreationPanel,
  },
  props: {
    unit_id: {
      required: true,
      default: null,
    },
    sellers: {
      required: true,
      default: [],
    },
    alarm_centers: {
      required: true,
      default: [],
    },
    customer: {
      required: true,
      default: null,
    },
    unit: {
      required: true,
      default: null,
    },
    updateInfo: {
      default: null,
    },
  },

  watch: {
    // SEC-373: the time fields are locked for Safe-4, so they show what the
    // devices get - the whole day - from the moment Safe-4 is the central
    // (picked, the unit's own central, or an order opened)
    isSafe4Selected(isSafe4) {
      if (isSafe4) this.applySafe4ContactTimes();
    },
    // ...and when the rows are replaced, e.g. by an order document import
    "order_alarm.contact_persons"() {
      this.applySafe4ContactTimes();
    },
    unit(newVal) {
      if (newVal) {
        this.order_alarm.seller = newVal.seller_id;
        this.order_alarm.alarm_center = newVal.alarm_central;
        this.order_alarm.from_address = `${newVal.address} ${newVal.zip_code} ${newVal.city}`;
        this.order_alarm.from_address_parts = { street: newVal.address || "", zip: newVal.zip_code || "", city: newVal.city || "" };
        this.selected_pdf_file = null;

        this.getDepartmentInfo();
      }
    },
    updateInfo: {
      // immediate: true ensures setupOrderForm runs when the component is
      // (re)created by vue-js-modal's :reset="true" with updateInfo already set.
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal) {
          this.setupOrderForm();
        } else if (oldVal !== undefined) {
          // Skip resetForm on the initial immediate call (oldVal is undefined).
          // Only reset when updateInfo is explicitly cleared after mount.
          this.resetForm();
        }
        // The catalog follows the order's country - reload it when the next
        // order (or a new one) belongs to another country than the loaded one.
        if (this.catalogCountry && this.catalogCountry !== this.orderCountry()) {
          this.fetchProducts();
          this.fetchSubcategories();
        }
        this.fetchCountryLists();
      },
    },
    "$i18n.locale"() {
      this.date_picker_langKey++;
    },
    "order_alarm.products": {
      deep: true,
      handler(products) {
        const alarm_center_categories = ['alarm'];
        const sim_card_categories = ['alarm', 'vehicle_tracker', 'display'];
        let ac = 0, sim = 0;
        for (const prod of products) {
          const qty = prod.qty || 0;
          if (qty === 0) continue;
          if (alarm_center_categories.includes(prod.category)) ac += qty;
          if (sim_card_categories.includes(prod.category)) sim += qty;
        }
        this.total_alarm_center_devices = ac;
        this.total_sim_devices = sim;
      },
    },
    // demo feedback: the order decides how many people can be described. Take
    // an alarm out of the order and the extra person blocks go with it, so the
    // count can never read "3 of 2". Blocks are dropped from the end; the ones
    // already filled in at the top are kept.
    maxPersonalAlarmBlocks(allowed) {
      const people = this.order_alarm.personal_alarms || [];
      if (people.length <= allowed) return;
      this.order_alarm.personal_alarms = people.slice(0, allowed);
      this.collapsedPersonalBlocks = this.collapsedPersonalBlocks.slice(0, allowed);
    },
  },

  data() {
    return {
      isLoading: false,
      selected_pdf_file: null,
      date_picker_langKey: 0,
      productsFromDB: [],
      // country of the loaded product catalog (see orderCountry)
      catalogCountry: null,
      // sellers/alarm centers of an order from another country than the
      // portal's - the sellers/alarm_centers props hold the portal's lists
      countryLists: { country: null, sellers: [], alarm_centers: [] },
      total_alarm_center_devices: 0,
      // Safe-4: the AlarmCenter _id that is Safe-4 (empty = integration off)
      safe4_center_id: "",
      total_sim_devices: 0,
      subcategories: [],
      expandedCategories: {},
      expandedSubcategories: {},
      expandedVariants: {},
      progressSaving: false,
      supportStaff: [],
      supportStaffLoadFailed: false,
      selectedAssignee: "",
      showChangeLog: false,
      // which personal blocks are folded away, so a long order stays readable
      collapsedPersonalBlocks: [],

      notificationContacts: [{ email: "", msisdn: "" }],
      accountWanted: false,
      order_alarm: {
        process_order: true,
        agreement_category: '',
        insurance: '',
        seller: "",
        contract_start: "",
        contract_period: "",
        contract_period_freetext: "",
        setup_fee: "",
        rental_month_cost: "",
        payment_terms: "",
        payment_terms_freetext: "",
        country: "",
        special_terms: "",
        sim_card: "Tele2",
        alarm_center: "",
        risk_profile: "",
        // demo feedback: the police case number
        reference_number: "",
        from_address: "",
        // Safe-4 (SEC-370): the address split up, only filled for Safe-4
        from_address_parts: { street: "", zip: "", city: "" },
        alarms_name: "",
        comment: "",
        info_securcloud: "",
        alarm_password: "",
        call_button: false,
        timer_alarm: false,
        man_down: false,
        nine_vib: false,
        direct_dispatch: false,
        security_company: "",
        security_company_phone: "",
        security_company_object_id: "",
        // demo feedback: delivery method replaces the yes/no direct shipping
        // question; direct_shipping is still written for the order board.
        delivery_method: "",
        delivery_tracking_link: "",
        direct_shipping: "",
        direct_shipping_address: "",
        sms_numbers: [],
        // demo feedback: notifications on alarm — general contacts and/or an
        // individually described setup
        notify_general: false,
        notify_individual: false,
        notification_individual_detail: "",
        notification_email: "",
        notification_phone: [],
        user_account: { firstname: "", lastname: "", email: "", phone: "", username: "", status: "", error: "" },
        // SEC-352: every account requested on the order, one row each.
        // user_account above is sent as the first of them.
        user_accounts: [{ firstname: "", lastname: "", email: "", phone: "", username: "", status: "", error: "", reason: "" }],

        selected_alarm_sort: "",
        personal_alarm: { reporting: "", risk_profile: "", name: "", home_address: "", work_address: "", transport: "", registration_number: "", model: "", color: "" },
        // demo feedback: one block per person, up to the number of alarm
        // devices ordered. personal_alarm above stays the first block, so
        // everything that already reads it keeps working.
        // demo feedback: there is always a first person to describe
        personal_alarms: [{
          reporting: "", name: "", home_address: "",
          transport: "", registration_number: "", model: "", color: "",
          risk_profile: "", from_address: "", alarms_name: "", alarm_password: "",
        }],
        contact_persons: [
          { name: "", function: "", phone: "", weekday: "", saturday: "", sunday: "" },
          { name: "", function: "", phone: "", weekday: "", saturday: "", sunday: "" },
          { name: "", function: "", phone: "", weekday: "", saturday: "", sunday: "" },
        ],
        delivery_period: "",
        products: [],
        software_service_cost: 0,
        hardware_cost: 0,
        // pdf info
        customer_info: {
          // contact
          contact_name: "",
          contact_email: "",
          contact_phone: "",
          company_name: "",
          // the business/unit name as written on the order document
          unit_name: "",
          invoice_detail: "",
          // general
          outdoor_work: "",
          alert_members: "",
          indoor_tracking: "",
          man_down_alert: "",
          email_sms_detail: "",
          // alarm plan
          total_user: "",
          person_group_names: "",
          work_task: "",
          working_place: "",
          risk_detail: "",
          other_info: "",
        },
      },
    };
  },

  computed: {
    // most recent change first (entries are stored in insertion order)
    sortedChangeLog() {
      return [...(this.order_alarm.change_log || [])].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
    },

    // device-type-specific checklist sections for this order (REQ-09);
    // orders without a matching device type fall back to the general list
    progressSections() {
      return checklistSectionsForProducts(this.order_alarm.products, "progress");
    },

    progressGeneralKeys() {
      return this.progressSections.general;
    },

    progressDeviceGroups() {
      return this.progressSections.groups.map((group) => ({
        key: group.key,
        entries: group.progress.map((item) => ({ key: `${group.key}.${item}`, item })),
      }));
    },

    // device products with no defined checklist items — flagged to support
    unmatchedDevices() {
      return this.progressSections.unmatched;
    },

    // every key of this order's progress checklist (general fallback +
    // namespaced device items)
    allProgressKeys() {
      return [
        ...this.progressGeneralKeys,
        ...this.progressDeviceGroups.flatMap((group) => group.entries.map((entry) => entry.key)),
      ];
    },

    allProgressChecked() {
      return this.allProgressKeys.every((key) => this.isProgressItemChecked(key));
    },
    riskProfileOptions() {
      return [
        { value: RISK_PROFILE.THREAT, label: this.$t('admin_order_alarm.threat_alarm') },
        { value: RISK_PROFILE.EMERGENCY, label: this.$t('admin_order_alarm.emergency_alarm') },
        // SEC-221 REQ-01: assault alarm — same label the device side uses
        { value: RISK_PROFILE.ASSAULT, label: this.$t('risk_profile.burglar_alarm') },
      ];
    },
    // Malmö's alarm central answers to its own risk profiles - the same three
    // the create-device page offers, kept as raw labels there, so the value
    // carries straight over to the devices created from this order.
    riskProfileOptionsMSL() {
      return [
        ...this.riskProfileOptions,
        { value: this.$t('alarm_create.malmo_fixed'), label: this.$t('alarm_create.malmo_fixed') },
        { value: this.$t('alarm_create.malmo_mobile'), label: this.$t('alarm_create.malmo_mobile') },
        { value: this.$t('alarm_create.malmo_emergency'), label: this.$t('alarm_create.malmo_emergency') },
      ];
    },

    showMalmoRiskProfiles() {
      const center = this.orderAlarmCenters.find((c) => c._id === this.order_alarm.alarm_center);
      if (!center) return false;
      return /(Malmö Stads Larmcentral)|(Skyresponse Malmö)/.test(center.name || '');
    },

    sim_operators() {
      return this.productsFromDB
        .filter(p => p.category === 'simcard_supplier')
        .map(p => p.name);
    },
    categoryOptions() {
      return [
        { value: 'alarm', label: this.$t('sales_dashboard.cat_personal_alarm') },
        { value: 'tag', label: this.$t('sales_dashboard.cat_tags') },
        { value: 'display', label: this.$t('sales_dashboard.cat_display') },
        { value: 'ajax', label: this.$t('sales_dashboard.cat_perimeter') },
        { value: 'vehicle_tracker', label: this.$t('sales_dashboard.cat_vehicle_tracker') },
        { value: 'software', label: this.$t('sales_dashboard.cat_software') },
        { value: 'simcard', label: this.$t('sales_dashboard.cat_simcard') },
        { value: 'accessory', label: this.$t('sales_dashboard.cat_accessory') },
        { value: 'other', label: this.$t('sales_dashboard.cat_other') }
      ];
    },
    user() {
      return this.$store.state.user;
    },
    date_picker_lang() {
      return this.$i18n.locale === 'no' ? 'nb' : this.$i18n.locale;
    },
    contractPeriodOptions() {
      const months = this.$t('admin_order_alarm.months');
      const options = [];
      for (let i = 0; i <= 120; i += 12) {
        options.push({ value: i, name: `${i} ${months}` });
      }
      options.push({ value: '__freetext__', name: this.$t('admin_order_alarm.contract_period_freetext') });
      return options;
    },
    isContractPeriodFreetext() {
      return this.order_alarm.contract_period === '__freetext__';
    },
    paymentTermsOptions() {
      return buildPaymentTermsOptions(this.orderCountry(), (key) => this.$t(key));
    },
    isPaymentTermsFreetext() {
      return this.order_alarm.payment_terms === '__fritekst__' || this.order_alarm.payment_terms === '__fritext__';
    },
    agreementCategoryOptions() {
      return [
        { value: 'rental', name: this.$t('admin_order_alarm.category_rental') },
        { value: 'cash_services', name: this.$t('admin_order_alarm.category_cash_services') },
        { value: 'financed', name: this.$t('admin_order_alarm.category_financed') }
      ];
    },
    insuranceOptions() {
      return [
        { value: 'yes', name: this.$t('yes') },
        { value: 'no', name: this.$t('no') }
      ];
    },
    yesNoOptions() {
      return [
        { value: 'yes', name: this.$t('yes') },
        { value: 'no', name: this.$t('no') }
      ];
    },
    // demo feedback: Personligt/Grupplarm on the order — same options and
    // personal-alarm follow-up fields as the create-device page.
    alarmSortOptions() {
      return [
        { value: ALARM_SORT.GROUP, label: this.$t('alarm_create.group_alarm') },
        { value: ALARM_SORT.PERSONAL, label: this.$t('alarm_create.personal') },
      ];
    },
    transportOptions() {
      return [
        { value: 'walking', label: this.$t('alarm_create.transport_walking') },
        { value: 'bike', label: this.$t('alarm_create.transport_bike') },
        { value: 'public_transport', label: this.$t('alarm_create.transport_public_transport') },
        { value: 'car', label: this.$t('alarm_create.transport_car') },
      ];
    },
    // the chosen risk profile as a code ('threat' / 'emergency' / 'assault'),
    // so orders that stored a translated label still match
    riskProfileCode() {
      return normalizeRiskProfile(this.order_alarm.risk_profile);
    },

    // The people carrying the alarms. Older orders hold a single
    // personal_alarm; it becomes the first block.
    personalAlarmBlocks() {
      return this.order_alarm.personal_alarms || [];
    },

    // One block per alarm device the order carries - AJAX units, tags and the
    // rest are not personal alarms, and the products watcher already counts
    // only the 'alarm' category.
    maxPersonalAlarmBlocks() {
      const alarms = (this.order_alarm.products || []).reduce(
        (count, product) => count + (product.category === 'alarm' ? (product.qty || 0) : 0),
        0
      );
      return Math.max(1, alarms);
    },

    canAddPersonalBlock() {
      return this.personalAlarmBlocks.length < this.maxPersonalAlarmBlocks;
    },

    isPersonalAlarm() {
      return normalizeAlarmSort(this.order_alarm.selected_alarm_sort) === ALARM_SORT.PERSONAL;
    },
    /** True when the order's alarm central is Safe-4 (SEC-370). */
    isSafe4Selected() {
      return !!this.safe4_center_id && this.order_alarm.alarm_center == this.safe4_center_id;
    },
    // Safe-4 (SEC-370): a Norwegian site's postcode must be inside the county
    // ranges (utils/safe4Postcodes.js) - the server refuses the device otherwise.
    safe4PostcodeRules() {
      // the unit's country first, the way the server decides it
      const country = (this.unit && this.unit.country) || this.orderCountry();
      return `required|safe4_postcode:${country}`;
    },
    // First contact person is mandatory like on the create-device page — but
    // only for new orders, so placed orders written as free text stay editable.
    firstContactRules() {
      return this.order_alarm.agreement_number ? '' : 'required';
    },
    accountCreated() {
      return (this.order_alarm.user_account || {}).status === 'created';
    },
    // The account is only created from the new-order flow; once the order is
    // placed (agreement number exists) the section is read-only — the
    // post-process save path does not touch it.
    accountLocked() {
      return this.accountCreated || Boolean(this.order_alarm.agreement_number);
    },
    // SEC-221 REQ-02 + demo feedback: the three alarm options as checkboxes
    // (unchecked = false by default) and the order booleans they are stored on.
    alarmOptionCheckboxes() {
      return [
        { field: 'call_button', label: this.$t('admin_order_alarm.internal_alarm') },
        { field: 'man_down', label: this.$t('admin_order_alarm.man_down') },
        { field: 'timer_alarm', label: this.$t('admin_order_alarm.timer_alarm') },
      ];
    },
    // demo feedback: how the devices reach the customer. Only "sent to
    // recipient" has a delivery address and a tracking link.
    deliveryMethodOptions() {
      return [
        { value: 'salesperson', name: this.$t('admin_order_alarm.delivery_to_salesperson') },
        { value: 'customer_pickup', name: this.$t('admin_order_alarm.delivery_customer_pickup') },
        { value: 'recipient', name: this.$t('admin_order_alarm.delivery_to_recipient') },
      ];
    },
    isDeliveryToRecipient() {
      return this.order_alarm.delivery_method === 'recipient';
    },
    showRentalPrice() {
      return ['rental', 'financed', 'cash_services'].includes(this.order_alarm.agreement_category);
    },
    showCashPrice() {
      return this.order_alarm.agreement_category === 'cash_services';
    },
    visibleProducts() {
      // All products are visible for all remaining agreement categories
      return this.order_alarm.products;
    },
    totalCashPrice() {
      let total = 0;
      this.order_alarm.products.forEach(p => {
        // For cash_services, exclude monthly products from cash sum
        if (p.type === 'simcard' || p.type === 'other_monthly' || p.type === 'addon_monthly') {
          return;
        }
        // Add main product
        if (p.qty > 0 && (p.cash_price || 0) > 0) {
          total += p.qty * (p.cash_price || 0);
        }
        // Add variants
        if (p.variants && p.variants.length > 0) {
          p.variants.forEach(v => {
            if (v.qty > 0 && (v.cash_price || 0) > 0) {
              total += v.qty * (v.cash_price || 0);
            }
          });
        }
      });
      return total;
    },
    hardwareProducts() {
      return this.visibleProducts.filter(p => !p.is_software && p.type !== 'simcard' && 
        p.type !== 'other_monthly' && p.type !== 'addon_monthly' && 
        p.type !== 'other_cash' && p.type !== 'addon_cash');
    },
    softwareProducts() {
      return this.visibleProducts.filter(p => p.is_software === true || 
        p.type === 'simcard' || p.type === 'other_monthly' || p.type === 'addon_monthly' || 
        p.type === 'other_cash' || p.type === 'addon_cash');
    },
    cashSumForRentalFinanced() {
      // For rental and financed: sum of Tillägg kontant + Övrigt kontant
      if (!['rental', 'financed'].includes(this.order_alarm.agreement_category)) return 0;
      let total = 0;
      this.order_alarm.products.forEach(p => {
        if ((p.type === 'addon_cash' || p.type === 'other_cash')) {
          // Add main product
          if (p.qty > 0 && (p.cash_price || 0) > 0) {
            total += p.qty * (p.cash_price || 0);
          }
          // Add variants
          if (p.variants && p.variants.length > 0) {
            p.variants.forEach(v => {
              if (v.qty > 0 && (v.cash_price || 0) > 0) {
                total += v.qty * (v.cash_price || 0);
              }
            });
          }
        }
      });
      return total;
    },
    calculatedCashSum() {
      // Return cash_sum based on agreement_category
      const category = this.order_alarm.agreement_category;
      if (category === 'rental' || category === 'financed') {
        return this.cashSumForRentalFinanced.toString();
      } else if (category === 'cash_services') {
        return this.totalCashPrice.toString();
      }
      return '';
    },
    calculateSoftwareServiceCost() {
      return this.computeSoftwareServiceCost(
        this.order_alarm,
        Number(this.getContractPeriodValue()) || 0
      );
    },
    calculateHardwareCost() {
      return this.computeHardwareCost(this.order_alarm);
    },
    // Group products by category
    groupedProducts() {
      const grouped = {};
      this.order_alarm.products.forEach(product => {
        const key = product.category || 'other';
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(product);
      });
      return grouped;
    },
    // Get subcategories for a specific category
    subcategoriesByParent() {
      const grouped = {};
      this.subcategories.forEach(sub => {
        if (!grouped[sub.parentCategory]) {
          grouped[sub.parentCategory] = [];
        }
        grouped[sub.parentCategory].push(sub);
      });
      // Sort subcategories by sortOrder
      Object.keys(grouped).forEach(key => {
        grouped[key].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      });
      return grouped;
    },
    // Categories that have products
    orderSellers() {
      return this.countryLists.country === this.orderCountry() ? this.countryLists.sellers : this.sellers;
    },

    orderAlarmCenters() {
      return this.countryLists.country === this.orderCountry() ? this.countryLists.alarm_centers : this.alarm_centers;
    },

    categoriesWithProducts() {
      return this.categoryOptions.filter(cat => 
        this.groupedProducts[cat.value] && this.groupedProducts[cat.value].length > 0
      );
    },
  },

  methods: {
    // Everything country-bound in the form (product catalog, sellers, alarm
    // centers, payment terms) follows the order's country, not the portal's.
    // A Norwegian order opened from the Swedish portal must be edited against
    // the Norwegian products - with the portal's catalog none of its saved
    // products match, and saving the order wipes them (also on the agreement).
    orderCountry() {
      return (this.updateInfo && this.updateInfo.country) || siteContext.getSiteContext();
    },

    async fetchProducts() {
      try {
        const country = this.orderCountry();
        this.catalogCountry = country;
        const response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/products`, { params: { country } });
        // a later load for another order's country has taken over
        if (country !== this.catalogCountry) return;
        this.productsFromDB = response.data.data;
        
        // Transform products to order format - include category/subcategory
        const transformedProducts = this.productsFromDB.map(p => ({
          _id: p._id,
          display_name: p.display_name || null,
          name: p.name,
          type: p.type,
          category: p.category || 'other',
          subcategory: p.subcategory || null,
          hasVariants: p.hasVariants || false,
          variants: p.variants || [],
          qty: 0,
          price: null,
          cash_price: null,
          is_software: p.is_software || false,
          always_visible: p.always_visible || false,
          purchase_price: p.purchase_price || 0,
          recommended_rental_price: p.recommended_rental_price || 0,
          recommended_cash_price: p.recommended_cash_price || 0
        }));
        
        // Initialize order products with transformed data
        this.order_alarm.products = transformedProducts;

        // If an admin is editing an existing order, re-apply saved quantities
        // over the freshly loaded full catalog (fixes the race where fetchProducts
        // resolves after setupOrderForm and overwrites saved values).
        // Sellers keep only the products stored on the order — do not merge.
        if (this.updateInfo && this.user.role === 'admin') {
          this.mergeProductsWithDB();
        }
      } catch (error) {
        console.error('Failed to fetch products:', error);
        this.handleError(error, 'Failed to load products');
      }
    },

    async fetchCountryLists() {
      // Only for a saved order: the portal's own lists (props) serve a new one.
      // Not compared to getSiteContext() - on the Swedish domain it can say
      // 'no' (Norwegian UI language) while the server still answers for 'se'.
      if (this.user.role !== 'admin' || !(this.updateInfo && this.updateInfo.country)) return;
      const country = this.updateInfo.country;
      if (this.countryLists.country === country) return;
      try {
        const params = { country };
        const [sellers, alarmCenters] = await Promise.all([
          this.axios.get(`${process.env.VUE_APP_SERVER_URL}/seller/reseller`, { params }),
          this.axios.get(`${process.env.VUE_APP_SERVER_URL}/alarm-central`, { params }),
        ]);
        if (country !== this.orderCountry()) return;
        this.countryLists = { country, sellers: sellers.data.data, alarm_centers: alarmCenters.data.data };
      } catch (error) {
        this.handleError(error);
      }
    },

    async fetchSubcategories() {
      try {
        const country = this.orderCountry();
        const response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/subcategories`, { params: { country } });
        if (country !== this.orderCountry()) return;
        this.subcategories = response.data.data;
      } catch (error) {
        console.error('Failed to fetch subcategories:', error);
      }
    },

    getPaymentTermsValue() {
      // If freetext is selected, return the custom text instead
      if (this.isPaymentTermsFreetext) {
        return this.order_alarm.payment_terms_freetext;
      }
      return this.order_alarm.payment_terms;
    },

    getContractPeriodValue() {
      // If freetext is selected, return the custom number instead
      if (this.isContractPeriodFreetext) {
        return this.order_alarm.contract_period_freetext;
      }
      return this.order_alarm.contract_period;
    },

    // Get products that belong to a specific subcategory within a category
    getProductsForSubcategory(categoryValue, subcategoryId) {
      const products = this.groupedProducts[categoryValue] || [];
      return products.filter(p => {
        if (!p.subcategory) return false;
        const productSubcatId = typeof p.subcategory === 'object' ? p.subcategory._id : p.subcategory;
        return String(productSubcatId) === String(subcategoryId);
      });
    },

    // Get products without any subcategory within a category
    getProductsWithoutSubcategory(categoryValue) {
      const products = this.groupedProducts[categoryValue] || [];
      return products.filter(p => !p.subcategory);
    },

    // Toggle category expansion
    toggleCategory(categoryValue) {
      this.$set(this.expandedCategories, categoryValue, !this.expandedCategories[categoryValue]);
    },

    // Toggle subcategory expansion
    toggleSubcategory(subcategoryId) {
      this.$set(this.expandedSubcategories, subcategoryId, !this.expandedSubcategories[subcategoryId]);
    },

    // Tpayment_terms_freetext expansion
    toggleVariants(productId) {
      this.$set(this.expandedVariants, productId, !this.expandedVariants[productId]);
    },

    // Get category label from value
    getCategoryLabel(categoryValue) {
      const cat = this.categoryOptions.find(c => c.value === categoryValue);
      return cat ? cat.label : categoryValue;
    },

    shouldShowRentalForProduct(product) {
      const category = this.order_alarm.agreement_category;
      
      // For rental and financed: show rental for all EXCEPT addon_cash and other_cash
      if (category === 'rental' || category === 'financed') {
        if (product.type === 'addon_cash' || product.type === 'other_cash') {
          return false;
        }
        return true;
      }
      
      // For cash_services: show rental for software and monthly products (including simcard)
      if (category === 'cash_services') {
        if (product.is_software === true) return true;
        if (product.type === 'simcard' || product.type === 'other_monthly' || product.type === 'addon_monthly') return true;
        return false;
      }
      
      return false;
    },

    getDepartmentInfo(){
      if(this.unit == null) return;
        this.order_alarm.customer_info.contact_name = this.unit.contact_name || "";
        this.order_alarm.customer_info.contact_email = this.unit.contact_email || "";
        this.order_alarm.customer_info.contact_phone = this.unit.contact_phone || "";
        // customer can be null when the order's customer_id matches no loaded
        // customer (e.g. redrawn orders) - throwing here aborts resetForm
        // inside before-close and leaves the modal open with emptied fields
        this.order_alarm.customer_info.company_name = (this.customer && this.customer.name) || this.order_alarm.customer_info.company_name || "";
        this.order_alarm.customer_info.invoice_detail = this.unit.billing_address + " " + this.unit.billing_zip_code + " " + this.unit.billing_city || "";  
    },
    
    shouldShowCashForProduct(product) {
      const category = this.order_alarm.agreement_category;
      
      // For rental and financed: only show cash for addon_cash and other_cash
      if (category === 'rental' || category === 'financed') {
        if (product.type === 'addon_cash' || product.type === 'other_cash') {
          return true;
        }
        return false;
      }
      
      // For cash_services: show cash for hardware and cash products
      if (category === 'cash_services') {
        if (product.type === 'addon_cash' || product.type === 'other_cash') return true;
        if (product.is_software !== true && product.type !== 'other_monthly' && product.type !== 'addon_monthly' && product.type !== 'simcard') return true;
        return false;
      }
      
      return false;
    },
    processOrderPDF() {
      if (this.selected_pdf_file != null) {
        let headers = {
          "Content-Type": "multipart/form-data",
          "X-Access-Token": this.$store.state.token,
        };

        let formData = new FormData();

        formData.append("pdf", this.selected_pdf_file);

        this.axios
          .post(`${process.env.VUE_APP_SERVER_URL}/admin-order/pdf`, formData, headers)
          .then((response) => {
            // eslint-disable-line
            const imported = this.applyOrderDocument(response.data || {});

            this.selected_pdf_file = null;
            this.$refs.pdfOrder.value = null;
            // tell support what came through - a printed copy the reader cannot
            // make sense of would otherwise look like a silent no-op
            if (imported > 0) this.handleSuccess(this.$t('admin_order_alarm.order_document_imported', { count: imported }));
            else this.handleError(null, this.$t('admin_order_alarm.order_document_empty'));
          })
          .catch((error) => {
            this.handleError(error);
            this.selected_pdf_file = null;
            this.$refs.pdfOrder.value = null;
          });
      }
    },

    // Why the alarm holder's account could not be created. An address that is
    // already in SecurCloud has its own wording (demo feedback) - anything
    // else repeats what the server said.
    accountFailureMessage(account) {
      if (account && account.reason === 'email_exists') {
        return this.$t('admin_order_alarm.account_exists');
      }
      return this.$t('admin_order_alarm.account_failed', { error: (account && account.error) || '' });
    },

    // ---- user accounts, one row per person (SEC-352) -----------------------
    emptyAccount() {
      return { firstname: "", lastname: "", email: "", phone: "", username: "", status: "", error: "", reason: "" };
    },

    // Who an account is, for the failure message: "Anna Svensson, anna@firma.se"
    accountSummary(account) {
      const name = `${account.firstname || ""} ${account.lastname || ""}`.trim();
      const email = (account.email || "").trim();
      return [name, email].filter(Boolean).join(", ");
    },

    // The email rules for one row. From the second row on, the email may not
    // repeat one of the rows above it (the rule gets their emails).
    // An email with "|" or "," is left out: vee-validate would split the rule
    // string on it and stop the whole form. The server still refuses it twice.
    accountEmailRules(index) {
      const earlierEmails = this.order_alarm.user_accounts
        .slice(0, index)
        .map((account) => (account.email || "").trim())
        .filter(Boolean)
        .filter((email) => !email.includes("|") && !email.includes(","));
      if (earlierEmails.length === 0) return "required|email";
      return `required|email|not_used_on_order:${earlierEmails.join(",")}`;
    },

    addAccountRow() {
      if (this.accountLocked) return;
      this.order_alarm.user_accounts.push(this.emptyAccount());
    },

    removeAccountRow(index) {
      if (this.accountLocked) return;
      if (this.order_alarm.user_accounts.length <= 1) return;
      this.order_alarm.user_accounts.splice(index, 1);
    },

    // What the order sends: the list, and the first account again as
    // user_account for everything that still reads the single account
    buildAccountsPayload() {
      const accounts = this.order_alarm.user_accounts || [];
      return {
        user_accounts: accounts,
        user_account: { ...this.emptyAccount(), ...(accounts[0] || {}) },
      };
    },

    // After placing the order: one message for the accounts that were made,
    // and one per account that could not be made, naming the person when
    // there are several. Older servers only answer with user_account.
    showAccountOutcomes(data) {
      let outcomes = [];
      if (data && Array.isArray(data.user_accounts)) outcomes = data.user_accounts;
      else if (data && data.user_account) outcomes = [data.user_account];

      const created = outcomes.filter((account) => account.status === 'created');
      const failed = outcomes.filter((account) => account.status === 'failed');

      if (created.length === 1) {
        this.handleSuccess(this.$t('admin_order_alarm.account_created', { username: created[0].username }));
      } else if (created.length > 1) {
        this.handleSuccess(this.$t('admin_order_alarm.accounts_created', { count: created.length }));
      }

      failed.forEach((account) => {
        const message = this.accountFailureMessage(account);
        if (outcomes.length > 1) this.handleError(null, `${this.accountSummary(account)}: ${message}`);
        else this.handleError(null, message);
      });
    },

    // ---- personal information, one block per person (demo feedback) -----
    emptyPersonalBlock() {
      return {
        // the person (demo feedback: no work address - the alarm's own
        // "from address" below already says where the work is)
        reporting: "", name: "", home_address: "",
        transport: "", registration_number: "", model: "", color: "",
        // and their own alarm. The order's comment is asked once, on the order.
        risk_profile: "", from_address: "", alarms_name: "", alarm_password: "",
      };
    },

    // demo feedback: copy one person's answer to everyone else, like the
    // create-devices modal does for the agreement number and the product name
    applyPersonFieldToAll(field, sourceIndex) {
      const source = this.order_alarm.personal_alarms[sourceIndex];
      const value = source ? source[field] : '';
      if (!String(value || '').trim()) return;
      this.order_alarm.personal_alarms.forEach((person, index) => {
        if (index === sourceIndex) return;
        this.$set(person, field, value);
      });
    },

    addPersonalBlock() {
      if (!this.canAddPersonalBlock) return;
      this.order_alarm.personal_alarms.push(this.emptyPersonalBlock());
      // the new block opens; the ones already there stay as the user left them
      this.collapsedPersonalBlocks.push(false);
    },

    removePersonalBlock(index) {
      if (this.order_alarm.personal_alarms.length <= 1) return;
      this.order_alarm.personal_alarms.splice(index, 1);
      this.collapsedPersonalBlocks.splice(index, 1);
    },

    expandPersonalBlocks() {
      this.collapsedPersonalBlocks = (this.order_alarm.personal_alarms || []).map(() => false);
    },

    togglePersonalBlock(index) {
      this.$set(this.collapsedPersonalBlocks, index, !this.collapsedPersonalBlocks[index]);
    },

    // The blocks as they are saved: the first one also stays in
    // personal_alarm, which the created devices and the older screens read.
    buildPersonalAlarmPayload() {
      // a group alarm describes no people: an order switched from personal to
      // group must not keep handing personal details to the created devices
      if (!this.isPersonalAlarm) {
        return { personal_alarms: [], personal_alarm: this.order_alarm.personal_alarm || {} };
      }

      const blocks = (this.order_alarm.personal_alarms || []).map((person) => ({ ...person }));
      const payload = {
        personal_alarms: blocks,
        personal_alarm: { ...(this.order_alarm.personal_alarm || {}), ...(blocks[0] || {}) },
      };
      // A group alarm answers risk profile, address and product name once, on
      // the order. A personal alarm answers them per person - the first
      // person's answers stay on the order too, so everything that reads the
      // order (board, documentation, older screens) still finds them.
      if (this.isPersonalAlarm && blocks[0]) {
        payload.risk_profile = blocks[0].risk_profile || this.order_alarm.risk_profile || '';
        payload.from_address = blocks[0].from_address || this.order_alarm.from_address || '';
        payload.alarms_name = blocks[0].alarms_name || this.order_alarm.alarms_name || '';
        payload.alarm_password = blocks[0].alarm_password || this.order_alarm.alarm_password || '';
      }
      return payload;
    },

    // ---- order-document import (demo feedback) --------------------------
    // Copy every answer the document holds into the form field it belongs to.
    // The document wins: importing it is an explicit action, and most of these
    // fields are pre-filled from the business (contact details, address), which
    // is exactly what the customer's own answers are meant to replace. Empty
    // answers never overwrite anything.
    // Returns how many fields were filled, for the message to support.
    applyOrderDocument(data) {
      const answer = (key) => String(data[key] || "").trim();
      let imported = 0;

      // `set` does the writing so every field is counted the same way.
      const fill = (set, value) => {
        if (!value) return;
        set(value);
        imported += 1;
      };
      const info = this.order_alarm.customer_info;
      const fillInfo = (key, value) => fill((v) => { this.$set(info, key, v); }, value);
      const fillOrder = (key, value) => fill((v) => { this.order_alarm[key] = v; }, value);

      // ---- Del 1: the orderer ----
      fillInfo("contact_name", answer("contact_name"));
      fillInfo("contact_email", answer("contact_email"));
      fillInfo("contact_phone", answer("contact_phone"));
      fillInfo("company_name", answer("company_name"));
      fillInfo("unit_name", answer("unit_name"));
      // the invoice reference belongs on the same line as the address
      const invoice = [answer("invoice_detail"), answer("invoice_reference")].filter(Boolean).join(" · ");
      fillInfo("invoice_detail", invoice);

      // ---- Del 2: the general questions ----
      // Yes/No questions come back as 'yes'/'no'; older documents answered
      // them in free text, which is kept as it was written.
      fillInfo("outdoor_work", this.orderDocumentAnswerText(answer("outdoor_work")));
      fillInfo("alert_members", this.orderDocumentAnswerText(answer("alert_members")));
      fillInfo("indoor_tracking", this.orderDocumentAnswerText(answer("indoor_tracking")));
      fillInfo("man_down_alert", this.orderDocumentAnswerText(answer("man_down_alert")));
      fillInfo("email_sms_detail", answer("email_sms_detail"));

      // the answers that also decide an alarm option
      if (this.isYesAnswer(answer("man_down_alert")) || this.isYesAnswer(answer("timer_alarm"))) {
        if (this.isYesAnswer(answer("man_down_alert"))) this.order_alarm.man_down = true;
        if (this.isYesAnswer(answer("timer_alarm"))) this.order_alarm.timer_alarm = true;
        imported += 1;
      }
      if (this.isYesAnswer(answer("alert_members")) || this.isYesAnswer(answer("internal_alarm"))) {
        this.order_alarm.call_button = true;
        imported += 1;
      }

      // the product name: the alarm model, else the person/group name
      fillOrder("alarms_name", answer("alarm_model") || answer("person_group_names"));

      // ---- Del 3: the alarm documentation ----
      // "no external alarm central" means no central on the devices
      if (answer("external_alarm_center") === "no") {
        this.order_alarm.alarm_center = "-";
        imported += 1;
      }
      fillInfo("total_user", answer("total_user"));
      fillInfo("person_group_names", answer("person_group_names"));
      fillInfo("work_task", answer("work_task"));
      fillInfo("working_place", answer("working_place"));
      fillInfo("risk_detail", answer("risk_detail"));
      fillInfo("other_info", answer("other_info"));
      fillOrder("from_address", answer("alarm_addr"));
      // Safe-4 (SEC-370): the one-line field is hidden, so the imported address
      // goes into street / postcode / city instead of being overwritten on save.
      // Filled whatever the central is now (SEC-373), so choosing Safe-4 after
      // the import still shows the imported address; for any other central the
      // parts are emptied on save (applySafe4Address).
      if (answer("alarm_addr")) this.order_alarm.from_address_parts = splitAddressLine(answer("alarm_addr"));
      fillOrder("alarm_password", answer("alarm_password"));
      fillOrder("reference_number", answer("reference_number"));

      // ---- Del 4: the contact persons ----
      imported += this.applyOrderDocumentContacts(data);

      // ---- notifications on alarm ----
      const contacts = this.parseNotificationContacts(answer("email_sms_detail"));
      const typedContacts = this.notificationContacts.some((c) => (c.email || "").trim() || (c.msisdn || "").trim());
      if (contacts.length && !typedContacts) {
        this.notificationContacts = contacts;
        this.order_alarm.notify_general = true;
        imported += 1;
      } else if (answer("notify_alarm") === "yes") {
        this.order_alarm.notify_general = true;
        imported += 1;
      }

      // ---- the first person of a personal alarm ----
      // the form describes people in blocks now; an imported document fills the
      // first of them. The workplace answer stays in the questionnaire above -
      // the person's own "from address" is what the alarm goes out from.
      const personal = (this.order_alarm.personal_alarms || [])[0] || this.order_alarm.personal_alarm;
      fill((v) => { this.$set(personal, 'home_address', v); }, answer("home_address"));
      fill((v) => { this.$set(personal, 'transport', v); }, this.matchTransportOption(answer("transport")));
      fill((v) => { this.$set(personal, 'name', v); }, answer("person_group_names"));
      fill((v) => { this.$set(personal, 'from_address', v); }, answer("alarm_addr"));
      fill((v) => { this.$set(personal, 'alarms_name', v); }, answer("alarm_model") || answer("person_group_names"));

      // ---- delivery ----
      if (answer("delivery_address")) {
        this.order_alarm.delivery_method = "recipient";
        fillOrder("direct_shipping_address", answer("delivery_address"));
      }
      fillOrder("delivery_tracking_link", answer("tracking_link"));

      // ---- what has no field of its own goes in the order comment ----
      imported += this.appendOrderDocumentNotes(data);

      return imported;
    },

    // The contact persons: the current document has a name/phone/email row per
    // person, older ones one free-text field each. Only fills the rows when
    // support has not typed any contact yet.
    applyOrderDocumentContacts(data) {
      const hasTyped = this.order_alarm.contact_persons.some((row) => this.isTypedContactRow(row));
      if (hasTyped) return 0;

      const rows = Array.isArray(data.contact_persons) ? data.contact_persons : [];
      if (rows.length) {
        const contacts = rows.map((row) => ({
          ...this.emptyContactRow(),
          name: String(row.name || "").trim(),
          phone: String(row.phone || "").trim(),
        }));
        while (contacts.length < 3) contacts.push(this.emptyContactRow());
        this.order_alarm.contact_persons = contacts;
        return 1;
      }

      const legacyText = [data.contact_person_1, data.contact_person_2, data.contact_person_3]
        .map((part) => String(part || "").replace(/^(?=\n)$|^\s*|\s*$|\n\n+/gm, ""))
        .filter(Boolean)
        .join("\n");
      if (!legacyText) return 0;

      this.order_alarm.contact_persons = this.parseLegacyContactPersons(legacyText);
      return 1;
    },

    // The answers the order form has no field for are added to the
    // order-handling comment so support still sees them: the number of alarms
    // the document asks for, the contact persons' e-mail addresses, and any
    // answer whose question the reader did not recognise (the document in a
    // language or revision it does not know) - those keep their question.
    appendOrderDocumentNotes(data) {
      const notes = [];
      const alarmCount = String(data.alarm_count || "").trim();
      if (alarmCount) notes.push(`${this.$t('admin_order_alarm.order_document_alarm_count')}: ${alarmCount}`);

      (Array.isArray(data.contact_persons) ? data.contact_persons : []).forEach((row, i) => {
        const email = String(row.email || "").trim();
        if (email) notes.push(`${this.$t('admin_order_alarm.contact_persons')} ${i + 1} ${this.$t('admin_order_alarm.email')}: ${email}`);
      });

      (Array.isArray(data.unmatched) ? data.unmatched : []).forEach((entry) => {
        const label = String(entry.label || "").trim();
        const value = String(entry.value || "").trim();
        if (value) notes.push(label ? `${label}: ${value}` : value);
      });

      const comment = String(this.order_alarm.comment || "");
      const missing = notes.filter((note) => !comment.includes(note));
      if (missing.length === 0) return 0;

      this.order_alarm.comment = [comment.trim(), ...missing].filter(Boolean).join("\n");
      return 1;
    },

    // 'yes'/'no' are shown in the reader's language; anything else (an older
    // document's free-text answer) is kept exactly as written.
    orderDocumentAnswerText(value) {
      if (value === "yes") return this.$t('yes');
      if (value === "no") return this.$t('no');
      return value;
    },

    orderNewAlarm() {
      this.$refs.formAdminOrderAlarm.validate().then(async (success) => {
        if (!success) {
          // a missing answer may sit in a folded person block - open them all
          // so the message is where the user can see it
          this.expandPersonalBlocks();
          return;
        }

        try {
          this.isLoading = true;

          // Update calculated fields before sending
          // demo feedback: the delivery method drives the old yes/no shipping
          // fields, which the order board and the packing flow still read.
          this.order_alarm.direct_shipping = this.isDeliveryToRecipient ? 'yes' : 'no';
          if (!this.isDeliveryToRecipient) {
            this.order_alarm.direct_shipping_address = '';
            this.order_alarm.delivery_tracking_link = '';
          }
          // demo feedback: pack-in-bag is hidden (irrelevant) for direct shipping
          // demo feedback: an unticked notification choice stores nothing
          if (!this.order_alarm.notify_general) this.notificationContacts = [{ email: "", msisdn: "" }];
          if (!this.order_alarm.notify_individual) this.order_alarm.notification_individual_detail = '';
          if (!this.order_alarm.direct_dispatch) {
            this.order_alarm.security_company = '';
            this.order_alarm.security_company_phone = '';
            this.order_alarm.security_company_object_id = '';
          }
          // demo feedback: "No" discards whatever was typed in the account fields
          if (!this.accountWanted) {
            this.order_alarm.user_account = {
              ...this.order_alarm.user_account,
              firstname: "", lastname: "", email: "", phone: "",
            };
            // SEC-352: and every account row with them
            this.order_alarm.user_accounts = [this.emptyAccount()];
          }
          // SEC-221 REQ-04: compose the contact pairs into the stored fields
          Object.assign(this.order_alarm, this.buildNotificationPayload());
          this.order_alarm.seller_name = this.getSellerName(this.order_alarm.seller);
          this.order_alarm.country = this.orderCountry();
          this.applySafe4Address();
          this.order_alarm.cash_sum = this.calculatedCashSum;
          // Store freetext values directly in payment_terms and contract_period
          this.order_alarm.payment_terms = this.getPaymentTermsValue();
          this.order_alarm.contract_period = this.getContractPeriodValue();
          this.order_alarm.software_service_cost = this.calculateSoftwareServiceCost;
          this.order_alarm.hardware_cost = this.calculateHardwareCost;
          
          // Format products for agreement creation (use separate copy to avoid mutating reactive data)
          const formattedProducts = this.formatProductsForAgreement();
          // demo feedback: the people carrying the alarms, with the first of
          // them also kept in personal_alarm for everything that reads it
          const orderPayload = {
            ...this.order_alarm,
            ...this.buildPersonalAlarmPayload(),
            // SEC-352: the account list, with the first also as user_account
            ...this.buildAccountsPayload(),
            products: formattedProducts,
            insurance: this.order_alarm.insurance === 'yes',
          };

          let response;
          if (this.order_alarm._id) {
            response = await this.axios.put(`${process.env.VUE_APP_SERVER_URL}/unit/${this.order_alarm.unit_id}/admin/order-alarm/${this.order_alarm._id}`, orderPayload);
          } else {
            response = await this.axios.post(`${process.env.VUE_APP_SERVER_URL}/unit/${this.unit_id}/admin/order-alarm`, orderPayload);
          }

          // SEC-221 REQ-03 + SEC-352: tell the user right away which of the
          // alarm holders' accounts were created (the status also shows when
          // the order is reopened).
          this.showAccountOutcomes(response.data);

          this.$emit("orderUpdated");

          if (this.order_alarm.process_order) this.handleSuccess(this.$t('admin_order_alarm.order') + " " + this.$t('user_actions.created'));
          else this.handleSuccess(this.$t('admin_order_alarm.order') + " " + this.$t('admin_order_alarm.save'));

          this.isLoading = false;

          this.hide("modal-admin-order-alarm");
          this.resetForm();
        } catch (error) {
          this.isLoading = false;
          // Safe-4 (SEC-370): the server refused values Safe-4 would not accept
          if (!this.handleSafe4Rejection(error, this.$refs.formAdminOrderAlarm)) this.handleError(error);
        }
      });
    },

    saveOrder() {
      this.$refs.formAdminOrderAlarm.validate().then(async (success) => {
        if (!success) {
          // a missing answer may sit in a folded person block - open them all
          // so the message is where the user can see it
          this.expandPersonalBlocks();
          return;
        }

        this.order_alarm.process_order = false;
        this.orderNewAlarm();
      });
    },

    updateOrderLog() {
      this.$refs.formAdminOrderAlarm.validate().then(async (success) => {
        if (!success) {
          // a missing answer may sit in a folded person block - open them all
          // so the message is where the user can see it
          this.expandPersonalBlocks();
          return;
        }

        try {
          this.isLoading = true;

          // Update calculated cash_sum before sending
          this.order_alarm.cash_sum = this.calculatedCashSum;
          this.order_alarm.software_service_cost = this.calculateSoftwareServiceCost;
          this.order_alarm.hardware_cost = this.calculateHardwareCost;
          // demo feedback: an unticked notification choice stores nothing.
          // Must happen before buildNotificationPayload() below, or unticking
          // "general notifications" would still save the contacts (and leave
          // them in the unit's SecurTool settings).
          if (!this.order_alarm.notify_general) this.notificationContacts = [{ email: "", msisdn: "" }];

          this.applySafe4Address();
          let data = {
            agreement_category: this.order_alarm.agreement_category,
            insurance: this.order_alarm.insurance === 'yes',
            seller: this.order_alarm.seller,
            contract_start: this.order_alarm.contract_start,
            contract_period: this.getContractPeriodValue(),
            setup_fee: this.order_alarm.setup_fee,
            rental_month_cost: this.order_alarm.rental_month_cost,
            cash_sum: this.order_alarm.cash_sum,
            payment_terms: this.getPaymentTermsValue(),
            special_terms: this.order_alarm.special_terms,
            sim_card: this.order_alarm.sim_card,
            alarm_center: this.order_alarm.alarm_center,
            risk_profile: this.order_alarm.risk_profile,
            // demo feedback: the police case number
            reference_number: this.order_alarm.reference_number || '',
            // demo feedback: Personligt/Grupplarm + personal-alarm info +
            // structured contact persons follow post-process edits too
            selected_alarm_sort: this.order_alarm.selected_alarm_sort || '',
            ...this.buildPersonalAlarmPayload(),
            contact_persons: this.order_alarm.contact_persons || [],
            from_address: this.order_alarm.from_address,
            from_address_parts: this.order_alarm.from_address_parts,
            alarms_name: this.order_alarm.alarms_name,
            comment: this.order_alarm.comment,
            info_securcloud: "",
            alarm_password: this.order_alarm.alarm_password,
            call_button: this.order_alarm.call_button === true,
            timer_alarm: this.order_alarm.timer_alarm === true,
            man_down: this.order_alarm.man_down === true,
            nine_vib: false,
            direct_dispatch: this.order_alarm.direct_dispatch,
            security_company: this.order_alarm.direct_dispatch ? (this.order_alarm.security_company || '') : '',
            security_company_phone: this.order_alarm.direct_dispatch ? (this.order_alarm.security_company_phone || '') : '',
            security_company_object_id: this.order_alarm.direct_dispatch ? (this.order_alarm.security_company_object_id || '') : '',
            // demo feedback: delivery method (the old yes/no fields follow it)
            delivery_method: this.order_alarm.delivery_method || '',
            delivery_tracking_link: this.isDeliveryToRecipient ? (this.order_alarm.delivery_tracking_link || '') : '',
            direct_shipping: this.isDeliveryToRecipient ? 'yes' : 'no',
            direct_shipping_address: this.isDeliveryToRecipient ? (this.order_alarm.direct_shipping_address || '') : '',
            sms_numbers: this.order_alarm.sms_numbers,
            // demo feedback: notifications on alarm
            notify_general: this.order_alarm.notify_general === true,
            notify_individual: this.order_alarm.notify_individual === true,
            notification_individual_detail: this.order_alarm.notify_individual ? (this.order_alarm.notification_individual_detail || '') : '',
            ...this.buildNotificationPayload(),
            delivery_period: this.order_alarm.delivery_period,
            products: this.formatProductsForAgreement(),
            software_service_cost: this.order_alarm.software_service_cost,
            hardware_cost: this.order_alarm.hardware_cost,
            customer_info: { ...this.order_alarm.customer_info, person_group_names: "" },
          };

          // eslint-disable-next-line
          let response = await this.axios.put(`${process.env.VUE_APP_SERVER_URL}/order-alarm/save/${this.order_alarm._id}`, data);

          this.$emit("orderUpdated");

          this.handleSuccess(this.$t('admin_order_alarm.order') + " " + this.$t('admin_order_alarm.update'));

          this.isLoading = false;

          this.hide("modal-admin-order-alarm");
          this.resetForm();
        } catch (error) {
          this.isLoading = false;
          // Safe-4 (SEC-370): the server refused values Safe-4 would not accept
          if (!this.handleSafe4Rejection(error, this.$refs.formAdminOrderAlarm)) this.handleError(error);
        }
      });
    },

    setupOrderForm() {
      if (this.updateInfo) {
        this.order_alarm = _.cloneDeep(this.updateInfo);
        this.order_alarm.process_order = true;
        // Safe-4 (SEC-370): orders saved before the split have no parts - they
        // start from the stored one-line address, so nothing has to be retyped
        const parts = this.order_alarm.from_address_parts || {};
        const hasParts = !!(parts.street || parts.zip || parts.city);
        this.$set(this.order_alarm, "from_address_parts", hasParts
          ? { street: parts.street || "", zip: parts.zip || "", city: parts.city || "" }
          : splitAddressLine(this.order_alarm.from_address));
        // Normalize legacy translated-label risk_profile to a canonical code.
        this.order_alarm.risk_profile = normalizeRiskProfile(this.order_alarm.risk_profile);
        // The alarm-option radios need strict booleans (SEC-221 REQ-02).
        this.order_alarm.call_button = this.order_alarm.call_button === true;
        this.order_alarm.man_down = this.order_alarm.man_down === true;
        this.order_alarm.timer_alarm = this.order_alarm.timer_alarm === true;
        // Orders created before the fields existed (SEC-221 REQ-06 + demo
        // feedback contractor contact details).
        this.$set(this.order_alarm, 'security_company', this.order_alarm.security_company || '');
        this.$set(this.order_alarm, 'security_company_phone', this.order_alarm.security_company_phone || '');
        this.$set(this.order_alarm, 'security_company_object_id', this.order_alarm.security_company_object_id || '');
        // SEC-221 REQ-04: edit the stored SecurTool-format contacts as pairs.
        this.$set(this.order_alarm, 'notification_email', this.order_alarm.notification_email || '');
        this.$set(this.order_alarm, 'notification_phone',
          Array.isArray(this.order_alarm.notification_phone) ? this.order_alarm.notification_phone : []);
        const savedEmails = this.order_alarm.notification_email.split(',').map((s) => s.trim()).filter(Boolean);
        const savedPhones = this.order_alarm.notification_phone;
        const pairCount = Math.max(savedEmails.length, savedPhones.length, 1);
        this.notificationContacts = Array.from({ length: pairCount }, (_, i) => ({
          email: savedEmails[i] || "",
          msisdn: savedPhones[i] ? savedPhones[i].msisdn : "",
        }));
        // demo feedback: notifications on alarm. Orders saved before the
        // checkboxes existed tick "general" when they carry any contact.
        this.$set(this.order_alarm, 'notification_individual_detail', this.order_alarm.notification_individual_detail || '');
        this.$set(this.order_alarm, 'notify_general',
          this.order_alarm.notify_general === true || savedEmails.length > 0 || savedPhones.length > 0);
        this.$set(this.order_alarm, 'notify_individual',
          this.order_alarm.notify_individual === true || Boolean(this.order_alarm.notification_individual_detail));
        // demo feedback: the police case number
        this.$set(this.order_alarm, 'reference_number', this.order_alarm.reference_number || '');
        // SEC-221 REQ-03: orders created before the account section existed
        this.$set(this.order_alarm, 'user_account', {
          firstname: "", lastname: "", email: "", phone: "", username: "", status: "", error: "",
          ...(this.order_alarm.user_account || {}),
        });
        // demo feedback: the Yes/No radio reflects whether an account was
        // requested on the saved order
        const savedAccount = this.order_alarm.user_account;
        this.accountWanted = Boolean(
          (savedAccount.firstname || "").trim() || (savedAccount.lastname || "").trim() ||
          (savedAccount.email || "").trim() || savedAccount.status
        );
        // SEC-352: one row per account. An order saved before the list
        // existed has only user_account - that becomes account 1.
        const hasAccountList = Array.isArray(this.order_alarm.user_accounts) && this.order_alarm.user_accounts.length > 0;
        const savedAccounts = hasAccountList ? this.order_alarm.user_accounts : [savedAccount];
        this.$set(this.order_alarm, 'user_accounts',
          savedAccounts.map((account) => ({ ...this.emptyAccount(), ...(account || {}) })));
        // demo feedback: Personligt/Grupplarm (+ personal-alarm info) and the
        // structured contact persons — normalize legacy orders to bindable shapes
        this.$set(this.order_alarm, 'selected_alarm_sort', normalizeAlarmSort(this.order_alarm.selected_alarm_sort || ''));
        this.$set(this.order_alarm, 'personal_alarm', {
          reporting: "", risk_profile: "", name: "", home_address: "", work_address: "",
          transport: "", registration_number: "", model: "", color: "",
          ...(this.order_alarm.personal_alarm || {}),
        });
        // demo feedback: one block per person. An order saved before the list
        // existed carries a single personal_alarm - that becomes block one.
        const savedPeople = Array.isArray(this.order_alarm.personal_alarms) ? this.order_alarm.personal_alarms : [];
        const people = savedPeople.length > 0
          ? savedPeople.map((person) => ({ ...this.emptyPersonalBlock(), ...(person || {}) }))
          : [{
            ...this.emptyPersonalBlock(),
            ...(this.order_alarm.personal_alarm || {}),
            // the order's own answers belong to the first person
            risk_profile: normalizeRiskProfile(this.order_alarm.risk_profile || ''),
            from_address: this.order_alarm.from_address || '',
            alarms_name: this.order_alarm.alarms_name || '',
            alarm_password: this.order_alarm.alarm_password || '',
          }];
        this.$set(this.order_alarm, 'personal_alarms', people);
        this.collapsedPersonalBlocks = people.map(() => false);
        const savedContacts = (Array.isArray(this.order_alarm.contact_persons) ? this.order_alarm.contact_persons : [])
          .map((row) => ({ ...this.emptyContactRow(), ...(row || {}) }));
        while (savedContacts.length < 3) savedContacts.push(this.emptyContactRow());
        const hasStructuredContacts = savedContacts.some((row) =>
          Object.values(row).some((value) => String(value || "").trim()));
        // orders written as free text ("Name 1, 23232323" per line) are
        // parsed into the structured rows so edits start from the old data
        this.$set(this.order_alarm, 'contact_persons',
          !hasStructuredContacts && this.order_alarm.contact_person
            ? this.parseLegacyContactPersons(this.order_alarm.contact_person)
            : savedContacts);

        // Some legacy orders have no customer_info at all (board loads with
        // plain .lean(), so schema defaults are not applied) — merge defaults
        // so the form fields always have an object to bind to.
        this.$set(this.order_alarm, 'customer_info', {
          contact_name: "",
          contact_email: "",
          contact_phone: "",
          company_name: "",
          unit_name: "",
          invoice_detail: "",
          outdoor_work: "",
          alert_members: "",
          indoor_tracking: "",
          man_down_alert: "",
          email_sms_detail: "",
          total_user: "",
          person_group_names: "",
          work_task: "",
          working_place: "",
          risk_detail: "",
          other_info: "",
          ...(this.order_alarm.customer_info || {}),
        });

        // fields added after launch are missing on older orders —
        // initialize them reactively so the form binds correctly.
        if (this.order_alarm.direct_shipping === undefined) this.$set(this.order_alarm, 'direct_shipping', '');
        if (this.order_alarm.direct_shipping_address === undefined) this.$set(this.order_alarm, 'direct_shipping_address', '');
        // demo feedback: delivery method. Orders saved with the old yes/no
        // question open as "sent to recipient" (yes) or blank, so support picks
        // between "responsible salesperson" and "customer picks up" once.
        this.$set(this.order_alarm, 'delivery_method',
          this.order_alarm.delivery_method || (this.order_alarm.direct_shipping === 'yes' ? 'recipient' : ''));
        this.$set(this.order_alarm, 'delivery_tracking_link', this.order_alarm.delivery_tracking_link || '');
        if (this.order_alarm.progress_checklist === undefined) this.$set(this.order_alarm, 'progress_checklist', []);
        if (this.order_alarm.change_log === undefined) this.$set(this.order_alarm, 'change_log', []);
        this.selectedAssignee = this.order_alarm.assignee || "";

        // Convert insurance from DB to string for dropdown
        // Handles boolean true/false, string "true"/"false", and string "yes"/"no"
        const ins = this.order_alarm.insurance;
        const needsAgreementFallback = !this.order_alarm.agreement_category;
        if (ins === true || ins === 'true' || ins === 'yes') {
          this.order_alarm.insurance = 'yes';
        } else if (ins === false || ins === 'false' || ins === 'no') {
          this.order_alarm.insurance = 'no';
        } else {
          // Insurance missing on order — try to fetch from the linked agreement
          this.order_alarm.insurance = '';
        }

        // If insurance or agreement_category is missing, fetch from the linked agreement
        if (!ins && ins !== false || needsAgreementFallback) {
          this.fetchFieldsFromAgreement();
        }

        // Admins see the full product catalog with saved quantities overlaid.
        // Sellers see only the products originally saved with the order.
        if (this.user.role === 'admin') {
          this.mergeProductsWithDB();
        }

        // Detect if payment_terms is freetext (doesn't match standard options)
        this.initializeFreetextPaymentTerms();
        
        // Detect if contract_period is freetext (doesn't match standard options)
        this.initializeFreetextContractPeriod();
      }
    },

    async fetchFieldsFromAgreement() {
      const agreementNumber = this.order_alarm.agreement_number;
      if (!agreementNumber) return;
      try {
        const response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/agreements`, {
          params: { search: agreementNumber, limit: 10, country: this.orderCountry() }
        });
        const agreements = response.data.agreements || response.data.data || [];
        const match = agreements.find(a => a.agreement_number === agreementNumber);
        if (!match) return;

        if (match.insurance !== undefined && match.insurance !== null && !this.order_alarm.insurance) {
          this.$set(this.order_alarm, 'insurance', match.insurance ? 'yes' : 'no');
        }
        if (match.agreement_category && !this.order_alarm.agreement_category) {
          this.$set(this.order_alarm, 'agreement_category', match.agreement_category);
        }
      } catch (e) {
        console.error('Failed to fetch fields from agreement:', e);
      }
    },

    // Rebuild the product list from the full DB catalog and overlay any saved
    // quantities/prices from the stored order.  This ensures that products added
    // to the catalogue after the order was created are also presented for editing,
    // and that previously ordered products retain their saved quantities.
    //
    // Always reads saved quantities from this.updateInfo.products (authoritative)
    // so that subsequent calls (e.g. after fetchProducts completes) are idempotent.
    mergeProductsWithDB() {
      if (!this.productsFromDB || this.productsFromDB.length === 0) return;
      if (!this.updateInfo) return;

      // Use updateInfo as the authoritative source of saved quantities so this
      // method is safe to call even after order_alarm.products has been overwritten.
      const savedProducts = this.updateInfo.products || [];

      // Three lookup strategies to cover all storage formats:
      //   1. By _id   – nested format (saved via updateOrderLog / post-process)
      //   2. By type  – flat format (saved via orderNewAlarm / formatProductsForAgreement,
      //                 which strips _id from main products)
      //   3. By variant_id – flat variant rows (both formats keep this field)
      const savedByProductId = new Map();
      const savedByType = new Map();
      const savedByVariantId = new Map();

      savedProducts.forEach(p => {
        if (p._id && !p.variant_id) {
          savedByProductId.set(String(p._id), p);
        }
        // Flat-format main products have no _id and no variant_id
        if (!p._id && !p.variant_id && p.type) {
          savedByType.set(p.type, p);
        }
        if (p.variant_id) {
          savedByVariantId.set(String(p.variant_id), p);
        }
      });

      this.order_alarm.products = this.productsFromDB.map(dbProd => {
        const savedProd =
          savedByProductId.get(String(dbProd._id)) ||
          savedByType.get(dbProd.type) ||
          null;

        return {
          _id: dbProd._id,
          display_name: dbProd.display_name || null,
          name: dbProd.name,
          type: dbProd.type,
          category: dbProd.category || 'other',
          subcategory: dbProd.subcategory || null,
          hasVariants: dbProd.hasVariants || false,
          variants: (dbProd.variants || []).map(v => {
            // Nested format: variant is embedded in the saved parent product
            const savedVariantNested = savedProd && Array.isArray(savedProd.variants)
              ? savedProd.variants.find(sv => String(sv._id) === String(v._id))
              : null;
            // Flat format: variant is a top-level entry keyed by variant_id
            const savedVariantFlat = savedByVariantId.get(String(v._id)) || null;
            const savedV = savedVariantNested || savedVariantFlat;

            return {
              ...v,
              qty: savedV ? (savedV.qty || 0) : 0,
              // SEC-405: the agreement form edits rental_price and leaves the
              // older price as it was, so rental_price wins when it is set
              price: savedV ? (savedV.rental_price ?? savedV.price ?? null) : null,
              cash_price: savedV ? (savedV.cash_price ?? null) : null,
              recommended_rental_price: v.recommended_rental_price || dbProd.recommended_rental_price || 0,
              recommended_cash_price: v.recommended_cash_price || dbProd.recommended_cash_price || 0,
              purchase_price: v.purchase_price || dbProd.purchase_price || 0,
            };
          }),
          qty: savedProd ? (savedProd.qty || 0) : 0,
          price: savedProd ? (savedProd.rental_price ?? savedProd.price ?? null) : null,
          cash_price: savedProd ? (savedProd.cash_price ?? null) : null,
          is_software: dbProd.is_software || false,
          always_visible: dbProd.always_visible || false,
          purchase_price: dbProd.purchase_price || 0,
          recommended_rental_price: dbProd.recommended_rental_price || 0,
          recommended_cash_price: dbProd.recommended_cash_price || 0,
        };
      });
    },
    
    initializeFreetextPaymentTerms() {
      if (!this.order_alarm || !this.order_alarm.payment_terms) return;
      
      // Standard payment terms values (both Norwegian and Swedish)
      const standardOptions = [
        'Årlig', 'Halvårlig', 'Kvartalsvis', 'Månedlig', '__fritekst__',  // Norwegian
        'Årsvis', 'Halv år', 'Kvartal', 'Månad', '__fritext__'  // Swedish
      ];
      
      const isFreetextMarker = this.order_alarm.payment_terms === '__fritekst__' || this.order_alarm.payment_terms === '__fritext__';
      
      // Case 1: payment_terms is already a freetext marker - ensure freetext field is initialized with $set for reactivity
      if (isFreetextMarker) {
        if (!this.order_alarm.payment_terms_freetext) {
          this.$set(this.order_alarm, 'payment_terms_freetext', '');
        }
        return;
      }
      
      // Case 2: payment_terms is not a standard option - it's a freetext value saved directly
      if (!standardOptions.includes(this.order_alarm.payment_terms)) {
        const isNorwegian = this.orderCountry() === 'no';
        // Store the original payment_terms value as freetext (use $set for reactivity)
        this.$set(this.order_alarm, 'payment_terms_freetext', this.order_alarm.payment_terms);
        // Set the dropdown to the freetext marker
        this.order_alarm.payment_terms = isNorwegian ? '__fritekst__' : '__fritext__';
      }
    },
    
    initializeFreetextContractPeriod() {
      if (!this.order_alarm || this.order_alarm.contract_period == null || this.order_alarm.contract_period === '') return;
      
      // Standard contract period values (0, 12, 24, 36, ..., 120)
      const standardOptions = [0, 12, 24, 36, 48, 60, 72, 84, 96, 108, 120];
      
      // Case 1: contract_period is already a freetext marker - ensure freetext field is initialized with $set for reactivity
      if (this.order_alarm.contract_period === '__freetext__') {
        if (!this.order_alarm.contract_period_freetext) {
          this.$set(this.order_alarm, 'contract_period_freetext', '');
        }
        return;
      }
      
      // Case 2: contract_period may be stored as a string in DB but represents a standard numeric option.
      // Coerce to number so Vue's select binding matches the numeric option values.
      const numericValue = Number(this.order_alarm.contract_period);
      if (!isNaN(numericValue) && standardOptions.includes(numericValue)) {
        this.order_alarm.contract_period = numericValue;
        return;
      }
      
      // Case 3: contract_period is a non-standard value - treat as freetext
      this.$set(this.order_alarm, 'contract_period_freetext', this.order_alarm.contract_period);
      this.order_alarm.contract_period = '__freetext__';
    },

    fileChangeCreate() {
      if (this.$refs.pdfOrder.files.length > 0) {
        this.selected_pdf_file = this.$refs.pdfOrder.files[0];
        this.processOrderPDF();
      } else {
        this.selected_pdf_file = null;
      }
    },

    calculateTotalPrice() {
      let total = 0;
      this.order_alarm.products.forEach(p => {
        // Calculate for main product
        const addProductToTotal = (product, qty, price) => {
          // For rental and financed: exclude addon_cash and other_cash
          if (this.order_alarm.agreement_category === 'rental' || this.order_alarm.agreement_category === 'financed') {
            if (product.type === 'addon_cash' || product.type === 'other_cash') {
              return 0;
            }
            return qty * (price || 0);
          }
          // For cash_services, include simcard, övrigt månad and tillägg månad in monthly cost
          else if (this.order_alarm.agreement_category === 'cash_services') {
            if (product.type === 'simcard' || product.type === 'other_monthly' || product.type === 'addon_monthly') {
              return qty * (price || 0);
            } else if (!product.is_software) {
              return 0;
            } else {
              return qty * (price || 0);
            }
          } else {
            return qty * (price || 0);
          }
        };
        
        // Add main product
        total += addProductToTotal(p, p.qty, p.price);
        
        // Add variants
        if (p.variants && p.variants.length > 0) {
          p.variants.forEach(v => {
            if (v.qty > 0) {
              total += addProductToTotal(p, v.qty, v.price);
            }
          });
        }
      });
      this.order_alarm.rental_month_cost = total;
    },

    formatProductsForAgreement() {
      // Filter products with qty > 0 and format for agreement creation
      const isCashAgreement = this.order_alarm.agreement_category === 'cash_services';
      const formattedProducts = [];
      
      this.order_alarm.products.forEach(p => {
        // Format main product if qty > 0
        if (p.qty > 0) {
          const formattedProduct = {
            display_name: p.display_name || p.name,
            name: p.name,
            type: p.type,
            category: p.category || 'other',
            is_software: p.is_software || false,
            qty: p.qty,
            purchase_price: p.purchase_price || 0
          };

          // SEC-405: a line that already is one variant (a seller edits the
          // order's own lines) keeps its variant, or the variants merge later
          if (p.variant_id) formattedProduct.variant_id = p.variant_id;
          if (p.variant_name) formattedProduct.variant_name = p.variant_name;
          if (p.articleNumber) formattedProduct.articleNumber = p.articleNumber;

          // Determine which price field to use based on agreement category and product type
          if (isCashAgreement) {
            const isCashProduct = p.type === 'addon_cash' || p.type === 'other_cash';
            const isHardware = !p.is_software && p.type !== 'simcard' && p.type !== 'other_monthly' && p.type !== 'addon_monthly';
            
            if (isCashProduct || isHardware) {
              formattedProduct.cash_price = p.cash_price || 0;
            } else {
              formattedProduct.price = p.price || 0;
            }
          } else {
            const isCashProduct = p.type === 'addon_cash' || p.type === 'other_cash';
            
            if (isCashProduct) {
              formattedProduct.cash_price = p.cash_price || 0;
            } else {
              formattedProduct.price = p.price || 0;
            }
          }

          formattedProducts.push(formattedProduct);
        }
        
        // Format variants with qty > 0
        if (p.variants && p.variants.length > 0) {
          p.variants.forEach(v => {
            if (v.qty > 0) {
              const formattedVariant = {
                display_name: `${p.display_name || p.name} - ${v.name}`,
                name: `${p.name} - ${v.name}`,
                type: p.type,
                category: p.category || 'other',
                variant_id: v._id,
                variant_name: v.name,
                articleNumber: v.articleNumber,
                is_software: p.is_software || false,
                qty: v.qty,
                purchase_price: v.purchase_price ?? p.purchase_price ?? 0
              };

              // Determine which price field to use
              if (isCashAgreement) {
                const isCashProduct = p.type === 'addon_cash' || p.type === 'other_cash';
                const isHardware = !p.is_software && p.type !== 'simcard' && p.type !== 'other_monthly' && p.type !== 'addon_monthly';
                
                if (isCashProduct || isHardware) {
                  formattedVariant.cash_price = v.cash_price || 0;
                } else {
                  formattedVariant.price = v.price || 0;
                }
              } else {
                const isCashProduct = p.type === 'addon_cash' || p.type === 'other_cash';
                
                if (isCashProduct) {
                  formattedVariant.cash_price = v.cash_price || 0;
                } else {
                  formattedVariant.price = v.price || 0;
                }
              }

              formattedProducts.push(formattedVariant);
            }
          });
        }
      });
      
      return formattedProducts;
    },

    resetForm() {
      // Reset expanded states for categories/subcategories/variants
      this.expandedCategories = {};
      this.expandedSubcategories = {};
      this.expandedVariants = {};
      this.notificationContacts = [{ email: "", msisdn: "" }];
      this.accountWanted = false;

      // Reset products with fresh data from DB
      let resetProducts = [];
      if (this.productsFromDB.length > 0) {
        // Use products from DB with recommended prices - include category/subcategory
        resetProducts = this.productsFromDB.map(p => ({
          _id: p._id,
          display_name: p.display_name || null,
          name: p.name,
          type: p.type,
          category: p.category || 'other',
          subcategory: p.subcategory || null,
          hasVariants: p.hasVariants || false,
          // Initialize variants with qty/price fields for ordering
          variants: (p.variants || []).map(v => ({
            ...v,
            qty: 0,
            price: null,
            cash_price: null,
            // Inherit from variant or fall back to parent product
            recommended_rental_price: v.recommended_rental_price || p.recommended_rental_price || 0,
            recommended_cash_price: v.recommended_cash_price || p.recommended_cash_price || 0,
            purchase_price: v.purchase_price || p.purchase_price || 0
          })),
          qty: 0,
          price: null,
          cash_price: null,
          is_software: p.is_software || false,
          always_visible: p.always_visible || false,
          recommended_rental_price: p.recommended_rental_price || 0,
          recommended_cash_price: p.recommended_cash_price || 0
        }));
      }

      this.order_alarm = {
        process_order: true,
        agreement_category: '',
        insurance: '',
        seller: "",
        contract_start: "",
        contract_period: "",
        setup_fee: "",
        rental_month_cost: "",
        payment_terms: "",
        special_terms: "",
        // demo feedback: preset to the most common SIM choice, still editable
        sim_card: "Tele2",
        alarm_center: "",
        risk_profile: "",
        // demo feedback: the police case number
        reference_number: "",
        from_address: "",
        // Safe-4 (SEC-370): the address split up, only filled for Safe-4
        from_address_parts: { street: "", zip: "", city: "" },
        alarms_name: "",
        comment: "",
        info_securcloud: "",
        alarm_password: "",
        call_button: false,
        timer_alarm: false,
        man_down: false,
        nine_vib: false,
        direct_dispatch: false,
        security_company: "",
        security_company_phone: "",
        security_company_object_id: "",
        // demo feedback: delivery method replaces the yes/no direct shipping
        // question; direct_shipping is still written for the order board.
        delivery_method: "",
        delivery_tracking_link: "",
        direct_shipping: "",
        direct_shipping_address: "",
        sms_numbers: [],
        // demo feedback: notifications on alarm — general contacts and/or an
        // individually described setup
        notify_general: false,
        notify_individual: false,
        notification_individual_detail: "",
        notification_email: "",
        notification_phone: [],
        user_account: { firstname: "", lastname: "", email: "", phone: "", username: "", status: "", error: "" },
        // SEC-352: every account requested on the order, one row each.
        // user_account above is sent as the first of them.
        user_accounts: [{ firstname: "", lastname: "", email: "", phone: "", username: "", status: "", error: "", reason: "" }],
        // demo feedback: Personligt/Grupplarm choice + personal-alarm info +
        // structured contact persons (create-device page's format)
        selected_alarm_sort: "",
        personal_alarm: { reporting: "", risk_profile: "", name: "", home_address: "", work_address: "", transport: "", registration_number: "", model: "", color: "" },
        // demo feedback: one block per person, up to the number of alarm
        // devices ordered. personal_alarm above stays the first block, so
        // everything that already reads it keeps working.
        // demo feedback: there is always a first person to describe
        personal_alarms: [{
          reporting: "", name: "", home_address: "",
          transport: "", registration_number: "", model: "", color: "",
          risk_profile: "", from_address: "", alarms_name: "", alarm_password: "",
        }],
        contact_persons: [
          { name: "", function: "", phone: "", weekday: "", saturday: "", sunday: "" },
          { name: "", function: "", phone: "", weekday: "", saturday: "", sunday: "" },
          { name: "", function: "", phone: "", weekday: "", saturday: "", sunday: "" },
        ],
        delivery_period: "",
        products: resetProducts,
        // pdf info
        customer_info: {
          // contact
          contact_name: "",
          contact_email: "",
          contact_phone: "",
          company_name: "",
          // the business/unit name as written on the order document
          unit_name: "",
          invoice_detail: "",
          // general
          outdoor_work: "",
          alert_members: "",
          indoor_tracking: "",
          man_down_alert: "",
          email_sms_detail: "",
          // alarm plan
          total_user: "",
          person_group_names: "",
          work_task: "",
          working_place: "",
          risk_detail: "",
          other_info: "",
        },
      };

      this.selectedAssignee = "";
      this.showChangeLog = false;

      if (this.unit) {
        this.order_alarm.seller = this.unit.seller_id;
        this.order_alarm.alarm_center = this.unit.alarm_central;
        this.order_alarm.from_address = `${this.unit.address} ${this.unit.zip_code} ${this.unit.city}`;
        this.order_alarm.from_address_parts = { street: this.unit.address || "", zip: this.unit.zip_code || "", city: this.unit.city || "" };
        this.getDepartmentInfo();
      }

      this.$refs.formAdminOrderAlarm.reset();
    },

    // "yes" from the reader, or a written "Ja" / "JA - till alla" / "Yes" /
    // "Tak" from an older document's free-text answer.
    isYesAnswer(text) {
      return /^(yes|ja|j|tak)\b/i.test(String(text || "").trim());
    },

    // Map a written transport answer to one of the dropdown values.
    matchTransportOption(text) {
      const answer = String(text || "").toLowerCase();
      if (!answer) return "";
      if (/bil|car/.test(answer)) return "car";
      if (/cykel|sykkel|bike/.test(answer)) return "bike";
      if (/kollektiv|buss|t_g|tåg|public/.test(answer)) return "public_transport";
      if (/g_ng|gång|gang|fots|walk/.test(answer)) return "walking";
      return "";
    },

    // Emails and mobile numbers written into one free-text answer, paired up
    // row by row. Numbers are normalized to the SecurTool format (46...).
    parseNotificationContacts(text) {
      const answer = String(text || "");
      const emails = answer.match(/[^\s,;]+@[^\s,;]+\.[^\s,;]+/g) || [];
      const numbers = (answer.match(/(\+?46|0)[\d\s-]{6,}/g) || [])
        .map((raw) => raw.replace(/[^\d]/g, ""))
        .map((digits) => (digits.startsWith("46") ? digits : `46${digits.replace(/^0+/, "")}`))
        .filter((digits) => digits.length >= 8);

      const rows = [];
      for (let i = 0; i < Math.max(emails.length, numbers.length); i++) {
        rows.push({ email: emails[i] || "", msisdn: numbers[i] || "" });
      }
      return rows;
    },

    // demo feedback: structured contact persons (create-device page's format)
    emptyContactRow() {
      return { name: "", function: "", phone: "", weekday: "", saturday: "", sunday: "" };
    },

    // Safe-4 (SEC-364 A3 / SEC-373): every contact person row gets the whole
    // day in all three time fields, as in the device forms. Other centrals
    // keep what was typed.
    applySafe4ContactTimes() {
      if (!this.isSafe4Selected) return;
      (this.order_alarm.contact_persons || []).forEach((contact) => {
        this.$set(contact, "weekday", SAFE4_CONTACT_TIME);
        this.$set(contact, "saturday", SAFE4_CONTACT_TIME);
        this.$set(contact, "sunday", SAFE4_CONTACT_TIME);
      });
    },

    // True when support typed something into a contact row. The Safe-4 times
    // the form fills in itself do not count, so they never stop an order
    // document import from filling in the contact persons.
    isTypedContactRow(row) {
      return Object.keys(row || {}).some((key) => {
        const value = String(row[key] || "").trim();
        const isSafe4Time = ["weekday", "saturday", "sunday"].includes(key) && value === SAFE4_CONTACT_TIME;
        return value !== "" && !isSafe4Time;
      });
    },

    // Safe-4 has no personal alarms (SEC-364 A16): picking Safe-4 drops that
    // choice. Only on the user's own change - loading an order never alters it.
    onAlarmCenterChange() {
      this.$nextTick(() => {
        if (this.isSafe4Selected && this.isPersonalAlarm) this.order_alarm.selected_alarm_sort = "";
      });
    },

    // Rules for a contact person's name / function: the order's own "required"
    // plus Safe-4's max length (SEC-364), so they match the device forms.
    orderContactRules(required, safe4Max) {
      const rules = required ? ["required"] : [];
      if (this.isSafe4Selected) rules.push(`max:${safe4Max}`);
      return rules.join("|");
    },

    // Safe-4 (SEC-370): the split address is what the devices get, and the
    // one-line from_address everything else reads is built from it. For any
    // other central the parts are emptied, so a device never inherits a stale
    // Safe-4 address.
    applySafe4Address() {
      const parts = this.order_alarm.from_address_parts || {};
      // Safe-4 config not loaded: leave both as they are rather than guess
      if (!this.safe4_center_id) return;
      if (this.isSafe4Selected && !this.isPersonalAlarm) {
        this.order_alarm.from_address = [parts.street, parts.zip, parts.city].map((v) => String(v || "").trim()).filter(Boolean).join(" ");
      } else {
        this.order_alarm.from_address_parts = { street: "", zip: "", city: "" };
      }
    },

    // Which alarm central is Safe-4, so the form can tell whether this order
    // is a Safe-4 order. Fails quietly -> no Safe-4 behavior.
    getSafe4CenterId() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/safe4/config`)
        .then((res) => { this.safe4_center_id = (res.data && res.data.safe4_alarm_center_id) || ""; })
        .catch(() => { this.safe4_center_id = ""; });
    },

    // Orders from before the structured rows stored contact persons as free
    // text, one person per line ("Name, 23232323" or "Name, Function, 123").
    // Parse each line into a row: the comma part that looks like a phone
    // number becomes the number, the first part the name, the rest the
    // function. Always returns at least the form's three rows.
    parseLegacyContactPersons(text) {
      const rows = String(text || "")
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const row = this.emptyContactRow();
          const parts = line.split(",").map((part) => part.trim()).filter(Boolean);
          if (!parts.length) return row;
          row.name = parts[0];
          const rest = parts.slice(1);
          const phoneIndex = rest.findIndex((part) => /^\+?[0-9][0-9 -]*$/.test(part));
          if (phoneIndex >= 0) row.phone = rest.splice(phoneIndex, 1)[0];
          row.function = rest.join(", ");
          return row;
        });
      while (rows.length < 3) rows.push(this.emptyContactRow());
      return rows;
    },

    // SEC-221 REQ-04: repeatable email/phone contact pairs
    addNotificationContact() {
      this.notificationContacts.push({ email: "", msisdn: "" });
    },

    removeNotificationContact(i) {
      this.notificationContacts.splice(i, 1);
      if (this.notificationContacts.length === 0) this.addNotificationContact();
    },

    // Compose the pairs into the SecurTool-format order fields:
    // notification_email = comma-separated addresses; notification_phone =
    // [{ msisdn, schedule, weekly_time }] with SecurTool's defaults. Phone
    // entries already stored on the order keep their schedule/weekly_time.
    buildNotificationPayload() {
      const emails = this.notificationContacts.map((c) => (c.email || "").trim()).filter(Boolean);

      const existingByMsisdn = new Map(
        (this.order_alarm.notification_phone || []).map((entry) => [entry.msisdn, entry])
      );
      const defaultDay = () => ({ start: "08:00", end: "17:00" });
      const phones = this.notificationContacts
        .map((c) => (c.msisdn || "").trim())
        .filter(Boolean)
        .map((msisdn) => existingByMsisdn.get(msisdn) || {
          msisdn,
          schedule: "allday",
          weekly_time: {
            mon: defaultDay(), tue: defaultDay(), wed: defaultDay(),
            thu: defaultDay(), fri: defaultDay(), sat: defaultDay(), sun: defaultDay(),
          },
        });

      return { notification_email: emails.join(", "), notification_phone: phones };
    },

    isNumber(evt) {
      evt = evt ? evt : window.event;
      var charCode = evt.which ? evt.which : evt.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        evt.preventDefault();
      } else {
        return true;
      }
    },

    getSellerName(seller_id) {
      if(!this.orderSellers || !seller_id) return "";
      let s = _.find(this.orderSellers, { _id: seller_id });
      return s ? s.name : "";
    },

    // assignee handling
    async fetchSupportStaff() {
      try {
        const response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/staffs/support`);
        this.supportStaff = response.data.data;
        this.supportStaffLoadFailed = false;
      } catch (error) {
        this.supportStaffLoadFailed = true;
        this.handleError(error);
      }
    },

    async saveAssignee() {
      try {
        const response = await this.axios.put(`${process.env.VUE_APP_SERVER_URL}/order-alarm/${this.order_alarm._id}/assignee`, {
          staff_id: this.selectedAssignee,
        });
        this.$set(this.order_alarm, "assignee", response.data.assignee);
        this.$set(this.order_alarm, "assignee_name", response.data.assignee_name);
        this.handleSuccess();
        this.$emit("orderUpdated");
      } catch (error) {
        // keep the stored assignment on failure
        this.selectedAssignee = this.order_alarm.assignee || "";
        this.handleError(error);
      }
    },

    // device item labels live under device_checklist_items; items shared
    // with the general lists reuse those labels
    progressItemLabel(item) {
      const keys = [
        `order_log.device_checklist_items.${item}`,
        `order_log.progress_checklist_items.${item}`,
      ];
      const key = keys.find((k) => this.$te(k));
      return key ? this.$t(key) : item;
    },

    // progress checklist
    isProgressItemChecked(key) {
      const item = (this.order_alarm.progress_checklist || []).find((i) => i.key === key);
      return item ? !!item.checked : false;
    },

    async toggleProgressItem(key, checked) {
      try {
        this.progressSaving = true;
        const response = await this.axios.put(`${process.env.VUE_APP_SERVER_URL}/order-alarm/${this.order_alarm._id}/checklist`, { key, checked });
        this.$set(this.order_alarm, "progress_checklist", response.data.progress_checklist);
        this.$emit("orderUpdated");
      } catch (error) {
        // re-render from the last saved state so the checkbox flips back
        this.$set(this.order_alarm, "progress_checklist", [...(this.order_alarm.progress_checklist || [])]);
        this.handleError(error);
      } finally {
        this.progressSaving = false;
      }
    },

    async setAllProgressItems(checked) {
      try {
        this.progressSaving = true;
        // the checklist definition is frontend-owned, so the order's full
        // key list is sent along for the server to include in the rebuild
        const keys = this.allProgressKeys;
        const response = await this.axios.put(`${process.env.VUE_APP_SERVER_URL}/order-alarm/${this.order_alarm._id}/checklist`, { check_all: true, checked, keys });
        this.$set(this.order_alarm, "progress_checklist", response.data.progress_checklist);
        this.$emit("orderUpdated");
      } catch (error) {
        this.$set(this.order_alarm, "progress_checklist", [...(this.order_alarm.progress_checklist || [])]);
        this.handleError(error);
      } finally {
        this.progressSaving = false;
      }
    },

    // change log rendering
    formatLogDate(date) {
      return date ? this.moment(date).format("YYYY-MM-DD HH:mm") : "";
    },

    logActionLabel(action) {
      const key = `order_log.log_actions.${action}`;
      return this.$te(key) ? this.$t(key) : action;
    },

    logActionClass(action) {
      const classes = {
        created: "bg-green-100 text-green-800",
        updated: "bg-blue-100 text-blue-800",
        stage_change: "bg-purple-100 text-purple-800",
        assignment: "bg-indigo-100 text-indigo-800",
        packing_checklist: "bg-teal-100 text-teal-800",
        progress_checklist: "bg-gray-200 text-gray-700",
        email_failed: "bg-red-100 text-red-800",
        devices_created: "bg-green-100 text-green-800",
      };
      return classes[action] || "bg-gray-200 text-gray-700";
    },

    // The stored description repeats what the translated badge already says
    // (e.g. "Order created") — only show it when it adds information.
    logDescription(entry) {
      if (!entry.description) return "";
      const hasTranslatedBadge = this.$te(`order_log.log_actions.${entry.action}`);
      if (entry.action === "email_failed" || !hasTranslatedBadge) return entry.description;
      return "";
    },

    fieldLabel(field) {
      if (!field) return "";
      if (field === "stage") return this.$t("order_log.status");
      if (field.startsWith("checklist.")) {
        const rest = field.slice(10);
        // device item: checklist.<group>.<item> -> "AJAX Hub – Reports in Ajax"
        const dot = rest.indexOf(".");
        if (dot !== -1) {
          const groupKey = `order_log.device_types.${rest.slice(0, dot)}`;
          if (this.$te(groupKey)) {
            return `${this.$t(groupKey)} – ${this.progressItemLabel(rest.slice(dot + 1))}`;
          }
        }
        const key = `order_log.progress_checklist_items.${rest}`;
        return this.$te(key) ? this.$t(key) : field;
      }
      const key = CHANGE_LOG_FIELD_LABEL_KEYS[field] || `admin_order_alarm.${field}`;
      return this.$te(key) ? this.$t(key) : field;
    },

    // One readable line per change: "jj" when set for the first time,
    // "jj → Anna" when changed; a cleared assignee reads "jj → No assignee".
    formatChange(c) {
      const cleared = c.field === "assignee" ? this.$t("order_log.no_assignee") : "–";
      const newLabel = c.new_value ? this.changeValueLabel(c.field, c.new_value) : cleared;
      if (!c.old_value) return newLabel;
      return `${this.changeValueLabel(c.field, c.old_value)} → ${newLabel}`;
    },

    // Show stage keys as their column labels and yes/no as Ja/Nej in the log
    changeValueLabel(field, value) {
      if (!value) return "–";
      if (field === "stage") {
        // column labels reuse the order_log keys; order_created's label key is "ordering"
        const key = `order_log.${value === "order_created" ? "ordering" : value}`;
        return this.$te(key) ? this.$t(key) : value;
      }
      if (value === "yes") return this.$t("yes");
      if (value === "no") return this.$t("no");
      return value;
    },

    show(id) {
      this.$modal.show(id);
    },

    hide(id) {
      this.resetForm();
      this.$refs.formAdminOrderAlarm.reset();
      this.$modal.hide(id);
    },
  },

  created() {
    this.modalWidth = window.innerWidth < MODAL_WIDTH ? window.innerWidth : MODAL_WIDTH;
  },

  async mounted() {
    if (this.user.role === "admin") {
      this.fetchSupportStaff();
    }
    this.getSafe4CenterId();
    await Promise.all([
      this.fetchProducts(),
      this.fetchSubcategories()
    ]);
  },
};
</script>
